// HT Calculator Wizard - Universal Integration for Regular & Styled Calculators
class HTCalculatorWizard {
  constructor() {
    this.currentStep = 1;
    this.calculator = null;
    this.wizardOverlay = null;
    this.isWizardMode = false;
    this.calculatorType = null; // 'regular' or 'styled'
    
    // Track field observers to clean them up
    this.fieldObservers = new Map();
    
    // Track field values to preserve them
    this.preservedValues = {
      quantity: '',
      print_sides: '',
      print_ink: ''
    };
    
    // Debouncing and update batching to prevent layout shifts
    this.updateQueue = new Set();
    this.isUpdating = false;
    this.initialLoadComplete = false;
    this.debouncedUpdate = this.debounce(this.processBatchedUpdates.bind(this), 50);
    
    // Additional options state (for Step 3 - regular calculator only)
    this.additionalOptions = {
      multiple_designs: false,
      variable_data: false,
      sequential_data: false,
      perforation: false
    };

    // Listen for cart success and reset wizard
    document.addEventListener('cart:item-added-success', () => {
      this.resetWizardComplete();
    });

    this.init();
  }

  // Dynamic step configuration based on calculator type
  getStepConfiguration() {
    if (this.calculatorType === 'styled') {
      // Get the selected style template's product type
      const styleTemplateField = this.getCalculatorField('style_template');
      const selectedOption = styleTemplateField ? styleTemplateField.selectedOptions[0] : null;
      let productType = selectedOption ? selectedOption.dataset.productType : '';
      
      // Normalize product types from plural metaobject values to singular for logic
      if (productType === 'folded_tags' || productType === 'folded-tags') {
        productType = 'folded_tag';
      } else if (productType === 'bottleneck_tags' || productType === 'bottleneck-tags') {
        productType = 'bottle_neck_tag';
      } else if (productType === 'car_hangers' || productType === 'car-hangers') {
        productType = 'car_hanger';
      } else if (productType === 'door_hangers' || productType === 'door-hangers') {
        productType = 'door_hanger';
      } else if (productType === 'swing_tags' || productType === 'swing-tags') {
        productType = 'swing_tag';
      }
      
      // Only folded_tag gets the Printing & Finishing step
      if (productType === 'folded_tag') {
        return {
          totalSteps: 3,
          steps: {
            1: {
              id: 'basics',
              title: 'Basics',
              fields: ['style_template', 'base', 'material', 'quantity', 'print_sides', 'print_ink'],
              requiredFields: ['style_template', 'base', 'material', 'quantity', 'print_sides', 'print_ink']
            },
            2: {
              id: 'printing-finishing',
              title: 'Printing & Finishing',
              fields: ['drill_diameter', 'drill_position', 'eyelet', 'string_attachment', 'string_type'],
              requiredFields: []
            },
            3: {
              id: 'additional',
              title: 'Additional',
              fields: ['multiple_designs', 'variable_data', 'sequential_data', 'perforation'],
              requiredFields: [],
              isCustomStep: true
            }
          }
        };
      } else {
        // All other styled products (including generic "styled" type)
        return {
          totalSteps: 2,
          steps: {
            1: {
              id: 'basics',
              title: 'Basics',
              fields: ['style_template', 'base', 'material', 'quantity', 'print_sides', 'print_ink'],
              requiredFields: ['style_template', 'base', 'material', 'quantity', 'print_sides', 'print_ink']
            },
            2: {
              id: 'additional',
              title: 'Additional',
              fields: ['multiple_designs', 'variable_data', 'sequential_data', 'perforation'],
              requiredFields: [],
              isCustomStep: true
            }
          }
        };
      }
    } else {
      // Regular calculator remains unchanged
      return {
        totalSteps: 3,
        steps: {
          1: {
            id: 'basics',
            title: 'Basics',
            fields: ['material', 'shape', 'size', 'custom_dimensions', 'quantity', 'print_sides', 'print_ink'],
            requiredFields: ['material', 'shape', 'quantity', 'print_sides', 'print_ink']
          },
          2: {
            id: 'printing-setup',
            title: 'Printing Setup Options',
            fields: ['corner_style', 'drill_diameter', 'drill_position', 'eyelet', 'string_attachment', 'string_type'],
            requiredFields: []
          },
          3: {
            id: 'additional',
            title: 'Additional',
            fields: ['multiple_designs', 'variable_data', 'sequential_data', 'perforation'],
            requiredFields: [],
            isCustomStep: true
          }
        }
      };
    }
  }

  // Reconfiguring for folded tags to add additional step in the personalisation process
  reconfigureWizardSteps() {
    const previousTotalSteps = this.totalSteps;
    const previousStep = this.currentStep;
    
    // Get new configuration based on current selection
    const config = this.getStepConfiguration();
    this.totalSteps = config.totalSteps;
    this.steps = config.steps;
    
    // If we're on a step that no longer exists, go back to step 1
    if (this.currentStep > this.totalSteps) {
      this.currentStep = 1;
      this.updateStepDisplay();
    } else if (previousTotalSteps !== this.totalSteps) {
      // Update progress indicator to show new step count
      this.rebuildProgressIndicator();
      this.updateProgressIndicator();
    }
  }

  // Add this method to rebuild the progress indicator HTML
  rebuildProgressIndicator() {
    const progressContainer = document.querySelector('.ht-wizard-steps');
    if (!progressContainer) return;
    
    progressContainer.innerHTML = this.createProgressSteps();
  }

  // Field mapping based on calculator type
  getCalculatorField(fieldName) {
    const fieldMaps = {
      regular: {
        'material': 'ht-material-select',
        'shape': 'ht-shape-select',
        'size': 'ht-size-select',
        'quantity': 'ht-quantity-select',
        'print_sides': 'ht-print-sides-select',
        'print_ink': 'ht-print-ink-select',
        'corner_style': 'ht-corner-style-select',
        'drill_diameter': 'ht-drill-diameter-select',
        'drill_position': 'ht-drill-position-select',
        'eyelet': 'ht-eyelet-select',
        'string_attachment': 'ht-string-attachment-select',
        'string_type': 'ht-string-type-select'
      },
      styled: {
        'material': 'ht-material-select',
        'style_template': 'ht-shape-select', // Note: styled calculator uses shapeSelect for style templates
        'base': 'ht-base-select',
        'size': 'ht-size-select',
        'quantity': 'ht-quantity-select',
        'print_sides': 'ht-print-sides-select',
        'print_ink': 'ht-print-ink-select',
        'drill_diameter': 'ht-drill-diameter-select',
        'drill_position': 'ht-drill-position-select',
        'eyelet': 'ht-eyelet-select',
        'string_attachment': 'ht-string-attachment-select',
        'string_type': 'ht-string-type-select'
      }
    };
    
    const fieldMap = fieldMaps[this.calculatorType] || fieldMaps.regular;
    const elementId = fieldMap[fieldName];
    return elementId ? document.getElementById(elementId) : null;
  }

  // Field labels based on calculator type
  getFieldLabel(fieldName) {
    // Dynamic labels for foil materials
    const isFoil = this.calculator && this.calculator.isFoilMaterial ? 
                   this.calculator.isFoilMaterial() : false;
    
    const labelMaps = {
      regular: {
        'material': 'Material',
        'shape': 'Shape',
        'size': 'Size',
        'quantity': 'Quantity',
        'print_sides': isFoil ? 'Foiled Sides' : 'Printed Sides',
        'print_ink': isFoil ? 'Foiled Colour' : 'Printed Ink',
        'corner_style': 'Corner Style',
        'drill_diameter': 'Drill Hole Diameter',
        'drill_position': 'Drill Hole Position',
        'eyelet': 'Eyelet',
        'string_attachment': 'String Supply',
        'string_type': 'String Type'
      },
      styled: {
        'material': 'Colour/Weight',
        'style_template': 'Style Template',
        'base': 'Material',
        'size': 'Size',
        'quantity': 'Quantity',
        'print_sides': isFoil ? 'Foiled Sides' : 'Printed Sides',
        'print_ink': isFoil ? 'Foiled Colour' : 'Printed Ink',
        'drill_diameter': 'Drill Hole Diameter',
        'drill_position': 'Drill Hole Position',
        'eyelet': 'Eyelet',
        'string_attachment': 'String Supply',
        'string_type': 'String Type'
      }
    };
    
    const labelMap = labelMaps[this.calculatorType] || labelMaps.regular;
    return labelMap[fieldName] || fieldName;
  }

  updatePrintFieldLabels() {
    if (!this.isWizardMode) return;

    // Find print_sides field label in current step
    const printSidesField = document.querySelector('[data-field="print_sides"]');
    if (printSidesField) {
      const label = printSidesField.querySelector('.ht-wizard-field-label');
      if (label) {
        label.textContent = this.getFieldLabel('print_sides');
      }
    }

    // Find print_ink field label in current step
    const printInkField = document.querySelector('[data-field="print_ink"]');
    if (printInkField) {
      const label = printInkField.querySelector('.ht-wizard-field-label');
      if (label) {
        label.textContent = this.getFieldLabel('print_ink');
      }
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  queueFieldUpdate(fieldName, updateFunction) {
    this.updateQueue.add({ fieldName, updateFunction });
    this.debouncedUpdate();
  }

  processBatchedUpdates() {
    if (this.isUpdating || this.updateQueue.size === 0) return;
    
    this.isUpdating = true;
    
    // Use immediate execution for faster updates
    const updates = Array.from(this.updateQueue);
    this.updateQueue.clear();
    
    updates.forEach(({ fieldName, updateFunction }) => {
      try {
        updateFunction();
      } catch (error) {
        // Silent error handling
      }
    });
    
    this.isUpdating = false;
    
    if (!this.initialLoadComplete) {
      this.initialLoadComplete = true;
    }
  }
  
  init() {
    const calculatorWrapper = document.querySelector('.ht-calculator-wrapper');
    if (!calculatorWrapper) {
      return;
    }
    
    if (calculatorWrapper.classList.contains('ht-wizard-enabled')) {
      this.enableWizardMode();
    } 
  }
  
  enableWizardMode() {
    this.waitForCalculator(() => {
      this.setupWizardInterface();
      this.bindWizardEvents();
      this.syncFieldsWithCalculator();
      this.isWizardMode = true;
    });
  }
  
  // Wait for both regular and styled calculators
  waitForCalculator(callback) {
    // Check for styled calculator first (priority for styled products)
    if (window.htCalculatorStyled && window.htCalculatorStyled.form) {
      this.calculator = window.htCalculatorStyled;
      this.calculatorType = 'styled';
      callback();
    } 
    // Fall back to regular calculator
    else if (window.htCalculator && window.htCalculator.form) {
      this.calculator = window.htCalculator;
      this.calculatorType = 'regular';
      callback();
    } 
    else {
      setTimeout(() => this.waitForCalculator(callback), 100);
    }
  }
  
  setupWizardInterface() {
    const calculatorContainer = document.querySelector('.ht-calculator-container');
    const wizardOverlay = document.querySelector('.ht-wizard-overlay');

    if (!calculatorContainer || !wizardOverlay) {
      return;
    }

    // Hide original calculator
    calculatorContainer.style.display = 'none';

    // Show wizard overlay
    wizardOverlay.style.display = 'block';
    this.wizardOverlay = wizardOverlay;

    // Initialize step configuration (structure already exists in HTML from Liquid)
    const config = this.getStepConfiguration();
    this.totalSteps = config.totalSteps;
    this.steps = config.steps;

    // Update the first step content (skeleton gets replaced)
    this.updateStepDisplay();
  }
  
  createWizardHTML() {
    const config = this.getStepConfiguration();
    this.totalSteps = config.totalSteps;
    this.steps = config.steps;
    
    return `
      <!-- Progress Indicator -->
      <div class="ht-wizard-progress">
        <div class="ht-wizard-steps">
          ${this.createProgressSteps()}
        </div>
      </div>

      <!-- Main Content -->
      <div class="ht-wizard-content">
        <div class="ht-wizard-form-section">
          <div class="ht-wizard-step-content" id="ht-wizard-step-content">
            <!-- Step content will be dynamically updated -->
          </div>
        </div>
        
        <!-- Live Summary -->
        <div class="ht-wizard-summary">
          <div class="ht-wizard-summary-header">
            <h3>✨ Live Summary</h3>
            <p>Auto-updates as you configure.</p>
          </div>
          
          <div class="ht-wizard-price-display">
            <div class="ht-wizard-price-label">Estimated total</div>
            <div class="ht-wizard-price-from">From</div>
            <div class="ht-wizard-price-excl">£0.00 <span>excl VAT</span></div>
            <div class="ht-wizard-price-incl">£0.00 <span>inc VAT</span></div>
          </div>
          
          <div class="ht-wizard-selections">
            <h4>Your selections</h4>
            <div class="ht-wizard-selections-grid" id="ht-wizard-selections-grid">
              <!-- Dynamic selections -->
            </div>
          </div>
          
          <div class="ht-wizard-completion-status">
            <span class="ht-wizard-status-text" id="ht-wizard-status-text">Choose material to begin.</span>
          </div>
        </div>
      </div>
      
      <!-- Navigation -->
      <div class="ht-wizard-navigation">
        <button type="button" class="ht-wizard-btn ht-wizard-btn--secondary" id="ht-wizard-back" style="display: none;">
          Back
        </button>
        <button type="button" class="ht-wizard-btn ht-wizard-btn--primary" id="ht-wizard-next">
          Continue
        </button>
      </div>

    `;
  }

  createProgressSteps() {
    let html = '';
    for (let i = 1; i <= this.totalSteps; i++) {
      const step = this.steps[i];
      const isActive = i === this.currentStep;
      const isCompleted = i < this.currentStep;
      
      let stepClass = 'ht-wizard-step';
      if (isActive) stepClass += ' ht-wizard-step--active';
      if (isCompleted) stepClass += ' ht-wizard-step--completed';
      
      html += `
        <div class="${stepClass}" data-step="${i}">
          <div class="ht-wizard-step-indicator">
            ${isCompleted ? '✓' : i}
          </div>
          <div class="ht-wizard-step-content">
            <div class="ht-wizard-step-title">${step.title}</div>
          </div>
        </div>
      `;
    }
    return html;
  }
  
  createStepContent() {
    const step = this.steps[this.currentStep];
    
    // Handle Additional step (custom step) regardless of step number
    if (step.isCustomStep) {
      return this.createStep3Content();
    }
    
    let html = `
      <div class="ht-wizard-step-header">
        <h2>${step.title}</h2>
        <p>${this.getStepDescription(this.currentStep)}</p>
      </div>
      <div class="ht-wizard-step-body">
    `;
    
    html += '<div class="ht-wizard-fields-grid">';
    
    // Process fields - handle custom dimensions specially
    step.fields.forEach(fieldName => {
      if (fieldName === 'custom_dimensions') {
        html += this.createCustomDimensionsField();
      } else {
        const field = this.getCalculatorField(fieldName);
        if (field) {
          html += this.createWizardField(fieldName, field);
        }
      }
    });
    
    html += '</div></div>';
    return html;
  }
  
  createCustomDimensionsField() {
    // Check if custom dimensions should be visible
    const shapeValue = this.calculator?.shapeSelect?.value;
    const sizeValue = this.calculator?.sizeSelect?.value;
    const shouldShow = shapeValue === 'custom' || sizeValue === 'custom_size';
    
    // Get current values from calculator
    const customWidth = this.calculator?.customWidthInput?.value || '';
    const customHeight = this.calculator?.customHeightInput?.value || '';
    
    // Get constraints from calculator
    const minSize = this.calculator?.customWidthInput?.min || 20;
    const maxWidth = this.calculator?.customWidthInput?.max || 440;
    const maxHeight = this.calculator?.customHeightInput?.max || 310;
    
    return `
      <div class="ht-wizard-custom-dimensions-container" id="ht-wizard-custom-dimensions-container" style="display: ${shouldShow ? 'block' : 'none'};">
        <div class="ht-wizard-custom-dimensions-header">
          <span class="ht-wizard-custom-dimensions-icon">ðŸ“</span>
          <span class="ht-wizard-custom-dimensions-title">Custom Dimensions</span>
        </div>
        <p class="ht-wizard-custom-dimensions-description">Enter your custom tag dimensions in millimetres</p>
        <div class="ht-wizard-custom-dimensions-fields">
          <div class="ht-wizard-field">
            <label for="ht-wizard-custom-width" class="ht-wizard-field-label">Width (mm)</label>
            <input type="number" 
                   id="ht-wizard-custom-width" 
                   class="ht-wizard-input" 
                   value="${customWidth}"
                   min="${minSize}" 
                   max="${maxWidth}"
                   placeholder="Enter width">
            <div class="ht-wizard-field-constraint">Min: ${minSize}mm, Max: ${maxWidth}mm</div>
          </div>
          <div class="ht-wizard-field">
            <label for="ht-wizard-custom-height" class="ht-wizard-field-label">Height (mm)</label>
            <input type="number" 
                   id="ht-wizard-custom-height" 
                   class="ht-wizard-input" 
                   value="${customHeight}"
                   min="${minSize}" 
                   max="${maxHeight}"
                   placeholder="Enter height">
            <div class="ht-wizard-field-constraint">Min: ${minSize}mm, Max: ${maxHeight}mm</div>
          </div>
        </div>
        <div class="ht-wizard-custom-dimensions-validation" id="ht-wizard-custom-dimensions-validation" style="display: none;"></div>
      </div>
    `;
  }
  
  createWizardField(fieldName, originalField) {
    const label = this.getFieldLabel(fieldName);
    const step = this.steps[this.currentStep];
    const isRequired = step.requiredFields.includes(fieldName);
    const fieldContainer = originalField.closest('.ht-calculator-field');
    const isHidden = fieldContainer && fieldContainer.style.display === 'none';
    const isDisabled = originalField.disabled;
    
    // Special handling for size field when custom shape/style is selected
    const shapeValue = this.calculator?.shapeSelect?.value;
    const shouldHideSizeField = fieldName === 'size' && shapeValue === 'custom';
    
    if (isHidden && !['drill_diameter', 'drill_position', 'string_type'].includes(fieldName)) {
      return '';
    }
    
    // Clone the select element
    const selectClone = originalField.cloneNode(true);
    selectClone.id = `ht-wizard-${fieldName}`;
    selectClone.classList.add('ht-wizard-select');
    selectClone.classList.remove('ht-calculator-select');
    // Add aria-label for accessibility (screen readers)
    selectClone.setAttribute('aria-label', label);
    
    // For styled calculator, respect the calculator's actual field state
    if (this.calculatorType === 'styled') {
      selectClone.disabled = originalField.disabled;
    } else {
      selectClone.disabled = isDisabled;
    }
    
    // Check if field is already completed (has value)
    const isCompleted = originalField.value && originalField.value !== '';
    
    // Add visual indicators
    let fieldClasses = `ht-wizard-field ${isCompleted ? 'ht-wizard-field--completed' : ''}`;
    if (selectClone.disabled) {
      fieldClasses += ' ht-wizard-field--disabled';
    }
    
    // Proper initial visibility for drill_position
    let initialStyle = '';
    if (fieldName === 'drill_position') {
      const drillDiameterCalc = this.getCalculatorField('drill_diameter');
      const shouldShowDrillPos = drillDiameterCalc && drillDiameterCalc.value && 
                                 drillDiameterCalc.value !== '' && drillDiameterCalc.value !== '0';
      
      if (!shouldShowDrillPos) {
        initialStyle = 'display: none;';
      }
    }
    
    let html = `
      <div class="${fieldClasses}" data-field="${fieldName}" id="ht-wizard-field-${fieldName}" style="${shouldHideSizeField ? 'display: none;' : ''}${initialStyle}">
        <label for="ht-wizard-${fieldName}" class="ht-wizard-field-label ${isRequired ? 'ht-wizard-field-required' : ''}">${label}</label>
        ${selectClone.outerHTML}
    `;
    
    // Add appropriate note based on field state
    if (selectClone.disabled) {
      let disabledMessage = 'Complete previous fields first';
      
      if (this.calculatorType === 'styled') {
        if (fieldName === 'material') {
          disabledMessage = 'Select style template first';
        } else if (fieldName === 'quantity') {
          disabledMessage = 'Select material first';
        } else if (fieldName === 'print_sides' || fieldName === 'print_ink') {
          disabledMessage = '';
        }
      } else {
        disabledMessage = '';
      }
      
      html += `<div class="ht-wizard-field-note ht-wizard-field-note--disabled">${disabledMessage}</div>`;
    } else {
      const note = this.getFieldNote(fieldName);
      if (note) {
        const noteId = fieldName === 'eyelet' ? ' id="ht-wizard-eyelet-note"' : '';
        html += `<div class="ht-wizard-field-note"${noteId}>${note}</div>`;
      }
    }
    
    html += '</div>';
    
    // ADD CUSTOM QUANTITY FIELD IF THIS IS QUANTITY SELECT
    if (fieldName === 'quantity') {
      html += this.createCustomQuantityField();
    }
    
    return html;
  }

  createCustomQuantityField() {
    // Check if custom quantity should be visible
    const quantityValue = this.calculator?.quantitySelect?.value;
    const shouldShow = quantityValue === 'custom';
    
    // Get current value from calculator
    const customQuantity = this.calculator?.customQuantityInput?.value || '';
    
    return `
    <div class="ht-wizard-custom-quantity-container" id="ht-wizard-custom-quantity-container" style="display: ${shouldShow ? 'contents' : 'none'};">
      <div class="ht-wizard-field">
        <label for="ht-wizard-custom-quantity" class="ht-wizard-field-label">Enter Custom Quantity</label>
        <input type="number" 
               id="ht-wizard-custom-quantity" 
               class="ht-wizard-input" 
               value="${customQuantity}"
               min="25" 
               placeholder="Minimum 25">
        <div class="ht-wizard-field-constraint">Minimum quantity: 25</div>
      </div>
    </div>
    `;
  }

  getFieldNote(fieldName) {
    const notes = {
      'drill_diameter': 'Additional service cost',
      'eyelet': 'Optional finishing',
      'drill_position': '\u00A0', // Non-breaking space for consistent vertical alignment
      'string_attachment': 'Additional service cost - from £4 '
    };
    return notes[fieldName] || '';
  }

  updateEyeletNote(isEyeletSelected) {
    const noteElement = document.getElementById('ht-wizard-eyelet-note');
    if (noteElement) {
      noteElement.textContent = isEyeletSelected 
        ? 'Drill hole set to 4mm for eyelets' 
        : 'Optional finishing';
    }
  }
  
  getStepDescription(stepNum) {
    if (this.calculatorType === 'styled') {
      const descriptions = {
        1: 'Choose material, style template, quantity, and printing options.',
        2: 'Optional finishing touches for your styled tags.'
      };
      return descriptions[stepNum] || '';
    } else {
      const descriptions = {
        1: 'Foundation of your tag.',
        2: 'Print and finishing settings.',
        3: 'Optional production features.'
      };
      return descriptions[stepNum] || '';
    }
  }
  
  // Enhanced field synchronization for both calculator types
  syncFieldsWithCalculator() {
    this.cleanupObservers();

    const step = this.steps[this.currentStep];
    
    if (this.steps[this.currentStep].isCustomStep) {
      this.bindStep3Fields();
      return;
    }
    
    step.fields.forEach(fieldName => {
      if (fieldName === 'custom_dimensions') {
        this.syncCustomDimensions();
      } else {
        const calculatorField = this.getCalculatorField(fieldName);
        const wizardField = document.getElementById(`ht-wizard-${fieldName}`);
        
        if (calculatorField && wizardField) {
          if (fieldName === 'print_sides' || fieldName === 'print_ink') {
            this.syncDynamicOptionsField(calculatorField, wizardField, fieldName);
          } else {
            this.syncRegularField(calculatorField, wizardField, fieldName);
          }
        }
      }
    });
    
    // ADD CUSTOM QUANTITY SYNC
    this.syncCustomQuantity();

    // Update labels after syncing fields (foil material detection)
    this.updatePrintFieldLabels();

    if (this.calculatorType === 'styled') {
      setTimeout(() => {
        this.syncStyledCalculatorFieldStates();
        this.syncPreselectedValues(); 
      }, 100);
    }
  }

  syncCustomQuantity() {
    const wizardCustomInput = document.getElementById('ht-wizard-custom-quantity');
    const calcCustomInput = this.calculator?.customQuantityInput;
    const wizardField = wizardCustomInput?.closest('.ht-wizard-field');
    const minQuantity = 25; // Minimum quantity requirement
    
    if (!wizardCustomInput || !calcCustomInput) {
      return;
    }
    
    // Sync initial value
    wizardCustomInput.value = calcCustomInput.value;
    
    // Check initial state for completion
    this.updateCustomQuantityCompletion(wizardCustomInput, wizardField, minQuantity);
    
    // Bind input event - wizard to calculator
    wizardCustomInput.addEventListener('input', (e) => {
      calcCustomInput.value = e.target.value;
      calcCustomInput.dispatchEvent(new Event('input'));
      calcCustomInput.dispatchEvent(new Event('change'));
      
      // Update completion state based on value
      this.updateCustomQuantityCompletion(wizardCustomInput, wizardField, minQuantity);
      
      this.updateWizardSummary();
      this.updateNavigationState();
    });
    
    // Observe calculator changes - calculator to wizard
    const observer = new MutationObserver(() => {
      if (wizardCustomInput.value !== calcCustomInput.value) {
        wizardCustomInput.value = calcCustomInput.value;
        this.updateCustomQuantityCompletion(wizardCustomInput, wizardField, minQuantity);
      }
    });
    
    observer.observe(calcCustomInput, {
      attributes: true,
      attributeFilter: ['value']
    });
    
    this.fieldObservers.set('custom_quantity', observer);
  }

  updateCustomQuantityCompletion(input, fieldContainer, minQuantity) {
    if (!input || !fieldContainer) return;
    
    const value = parseInt(input.value, 10);
    
    // Remove both classes first
    fieldContainer.classList.remove('ht-wizard-field--completed', 'ht-wizard-field--invalid');
    
    if (value >= minQuantity) {
      // Met minimum - turn green (completed)
      fieldContainer.classList.add('ht-wizard-field--completed');
    } else if (input.value !== '') {
      // Below minimum but has value - show as invalid
      fieldContainer.classList.add('ht-wizard-field--invalid');
    }
    // If empty value, no classes (neutral state)
  }

  validateStyledCalculatorStep1() {
    const errors = [];
    let isValid = true;
    
    // Always require style template first
    const styleTemplateField = this.getCalculatorField('style_template');
    if (!styleTemplateField || !styleTemplateField.value || styleTemplateField.value === '') {
      isValid = false;
      errors.push('style_template');
      // If no style template, don't validate downstream fields
      return { isValid, errors };
    }
    
    // Check if material field is enabled and has value
    const materialField = this.getCalculatorField('material');
    if (materialField && !materialField.disabled) {
      if (!materialField.value || materialField.value === '') {
        isValid = false;
        errors.push('material');
        // If no material, don't validate quantity yet
        return { isValid, errors };
      }
    }
    
    // Enhanced quantity validation with multiple source checks
    const quantityField = this.getCalculatorField('quantity');
    if (quantityField && !quantityField.disabled) {
      // Check multiple sources for quantity value due to async sync issues
      const calcValue = quantityField.value;
      const wizardQuantityField = document.getElementById('ht-wizard-quantity');
      const wizardValue = wizardQuantityField ? wizardQuantityField.value : '';
      
      // Also check the calculator's internal quantity method
      let calculatorQuantity = 0;
      if (this.calculator && this.calculator.getCurrentQuantity) {
        try {
          calculatorQuantity = this.calculator.getCurrentQuantity();
        } catch (e) {
          // Silent error handling
        }
      }
      
      // Consider quantity valid if ANY of these sources has a value
      const hasValidQuantity = (calcValue && calcValue !== '' && calcValue !== '0') || 
                             (wizardValue && wizardValue !== '' && wizardValue !== '0') ||
                             (calculatorQuantity > 0);
      
      if (!hasValidQuantity) {
        isValid = false;
        errors.push('quantity');
        return { isValid, errors };
      }
    }

    const printSidesField = this.getCalculatorField('print_sides');
    if (!printSidesField || printSidesField.disabled || !printSidesField.value || printSidesField.value === '') {
      isValid = false;
      errors.push('print_sides');
    }

    const printInkField = this.getCalculatorField('print_ink');
    if (!printInkField || printInkField.disabled || !printInkField.value || printInkField.value === '') {
      isValid = false;
      errors.push('print_ink');
    }

    return { isValid, errors };
  }

  // Enhanced validation for both calculator types
  validateCurrentStep() {
    const step = this.steps[this.currentStep];
    const errors = [];
    let isValid = true;
    
    // Step 3 has no required fields, always valid (regular calculator only)
    if (step.isCustomStep) {
      return { isValid: true, errors: [] };
    }
    
    // STYLED CALCULATOR: Use specialized validation
    if (this.calculatorType === 'styled' && this.currentStep === 1) {
      return this.validateStyledCalculatorStep1();
    }
    
    // STYLED CALCULATOR: Step 2 validation
      if (this.calculatorType === 'styled' && this.currentStep === 2) {
        // Check if this is a Folded Tag (has Printing & Finishing step)
        const step = this.steps[this.currentStep];

        if (step && step.id === 'printing-finishing') {
          // FOLDED TAGS: Validate drill_diameter is explicitly selected
          const drillDiameterField = this.getCalculatorField('drill_diameter');
          const drillPositionField = this.getCalculatorField('drill_position');
          const drillDiameterContainer = document.getElementById('ht-drill-diameter-field');

          // MANDATORY: Drill diameter must be explicitly selected (including "None")
          if (drillDiameterContainer &&
              drillDiameterContainer.style.display !== 'none' &&
              drillDiameterField && !drillDiameterField.disabled) {
            if (!drillDiameterField.value || drillDiameterField.value === '') {
              isValid = false;
              if (!errors.includes('drill_diameter')) {
                errors.push('drill_diameter');
              }
            }
          }

          // If drill diameter selected (not "None"), position is required
          const isNoneDiameter = !drillDiameterField.value ||
                                 drillDiameterField.value === '0' ||
                                 drillDiameterField.value.toLowerCase().includes('none');

          if (drillDiameterField && drillDiameterField.value && !isNoneDiameter) {
            if (!drillPositionField || !drillPositionField.value || drillPositionField.value === '') {
              isValid = false;
              if (!errors.includes('drill_position')) {
                errors.push('drill_position');
              }
            }
          }

          // If String Supply is supplying string, String Type must be selected.
          // Only require it when String Supply is genuinely shown to the customer
          // (the wizard drops a hidden string_attachment field on render).
          const stringAttachmentField = this.getCalculatorField('string_attachment');
          const stringTypeField = this.getCalculatorField('string_type');
          const stringAttachmentContainer = stringAttachmentField
            ? stringAttachmentField.closest('.ht-calculator-field')
            : null;
          const isStringAttachmentVisible = stringAttachmentField &&
            !stringAttachmentField.disabled &&
            (!stringAttachmentContainer || stringAttachmentContainer.style.display !== 'none');

          if (isStringAttachmentVisible && this.isStringSupplied(stringAttachmentField.value)) {
            if (!stringTypeField || !stringTypeField.value || stringTypeField.value === '') {
              isValid = false;
              if (!errors.includes('string_type')) {
                errors.push('string_type');
              }
            }
          }

          return { isValid, errors };
        }

        // OTHER STYLED PRODUCTS: Original validation (print_sides, print_ink)
        const printSidesField = this.getCalculatorField('print_sides');
        if (!printSidesField || printSidesField.disabled || !printSidesField.value || printSidesField.value === '')
   {
          isValid = false;
          errors.push('print_sides');
        }

        const printInkField = this.getCalculatorField('print_ink');
        if (!printInkField || printInkField.disabled || !printInkField.value || printInkField.value === '') {
          isValid = false;
          errors.push('print_ink');
        }

        return { isValid, errors };
      }
    
    // REGULAR CALCULATOR: Original validation logic
    // Only validate required fields, and skip disabled ones
    step.requiredFields.forEach(fieldName => {
      const field = this.getCalculatorField(fieldName);
      
      // Skip validation if field is disabled or hidden
      if (!field || field.disabled) {
        return;
      }
      
      // Check if field container is hidden
      const fieldContainer = field.closest('.ht-calculator-field');
      if (fieldContainer && fieldContainer.style.display === 'none') {
        return;
      }
      
      // Special handling for shape/style_template when it's custom
      if ((fieldName === 'shape' || fieldName === 'style_template')) {
        if (!field || !field.value || field.value === '') {
          isValid = false;
          errors.push(fieldName);
        }
      }
      // Don't require size when shape/style is custom
      else if (fieldName === 'size' && this.calculator?.shapeSelect?.value === 'custom') {
        // Skip size validation for custom shape/style
      }
      else if (field && (!field.value || field.value === '')) {
        isValid = false;
        errors.push(fieldName);
      }
    });
    
    // Special validation for custom size/dimensions (only for step 1)
    if (this.currentStep === 1) {
      const shapeValue = this.calculator?.shapeSelect?.value;
      const sizeValue = this.calculator?.sizeSelect?.value;
      
      if (shapeValue === 'custom' || sizeValue === 'custom_size') {
        // Must have valid custom dimensions
        if (!this.calculator.hasValidCustomSize()) {
          isValid = false;
          if (!errors.includes('custom_dimensions')) {
            errors.push('custom_dimensions');
          }
        }
      }
      // Normal size validation when not custom
      else if (shapeValue !== 'custom') {
        const sizeField = this.getCalculatorField('size');
        if (!sizeField || !sizeField.value || sizeField.value === '') {
          if (!errors.includes('size')) {
            isValid = false;
            errors.push('size');
          }
        }
      }
    }

    if (this.currentStep === 2 && this.calculatorType === 'regular') {
        const drillDiameterField = this.getCalculatorField('drill_diameter');
        const drillPositionField = this.getCalculatorField('drill_position');
        const drillDiameterContainer = document.getElementById('ht-drill-diameter-field');

        // MANDATORY: Drill diameter must be explicitly selected (including "None")
        // Only validate if the field is visible and enabled (shape supports drill holes)
        if (drillDiameterContainer &&
            drillDiameterContainer.style.display !== 'none' &&
            drillDiameterField && !drillDiameterField.disabled) {
          // Check if still on placeholder (empty value)
          if (!drillDiameterField.value || drillDiameterField.value === '') {
            isValid = false;
            if (!errors.includes('drill_diameter')) {
              errors.push('drill_diameter');
            }
          }
        }

        // If drill diameter is selected (not "None"), position is required
        const isNoneDiameter = !drillDiameterField.value ||
                               drillDiameterField.value === '0' ||
                               drillDiameterField.value.toLowerCase().includes('none');

        if (drillDiameterField && drillDiameterField.value && !isNoneDiameter) {
          if (!drillPositionField || !drillPositionField.value || drillPositionField.value === '') {
            isValid = false;
            if (!errors.includes('drill_position')) {
              errors.push('drill_position');
            }
          }
        }

        // If String Supply is supplying string, String Type must be selected.
        const stringAttachmentField = this.getCalculatorField('string_attachment');
        const stringTypeField = this.getCalculatorField('string_type');
        const stringAttachmentContainer = stringAttachmentField
          ? stringAttachmentField.closest('.ht-calculator-field')
          : null;
        const isStringAttachmentVisible = stringAttachmentField &&
          !stringAttachmentField.disabled &&
          (!stringAttachmentContainer || stringAttachmentContainer.style.display !== 'none');

        if (isStringAttachmentVisible && this.isStringSupplied(stringAttachmentField.value)) {
          if (!stringTypeField || !stringTypeField.value || stringTypeField.value === '') {
            isValid = false;
            if (!errors.includes('string_type')) {
              errors.push('string_type');
            }
          }
        }
      }

    return { isValid, errors };
  }

  syncDynamicOptionsField(calculatorField, wizardField, fieldName) {
    wizardField.value = calculatorField.value;
    
    wizardField.addEventListener('change', (e) => {
      calculatorField.value = e.target.value;
      calculatorField.dispatchEvent(new Event('change'));
      
      this.queueFieldUpdate(fieldName, () => {
        this.updateWizardSummary();
        this.updateFieldCompletionState(wizardField);
        this.updateNavigationState();
      });
    });
    
    const observer = new MutationObserver((mutations) => {
      let needsSync = false;
      
      mutations.forEach(mutation => {
        if (mutation.type === 'childList' || 
            (mutation.type === 'attributes' && mutation.attributeName === 'disabled')) {
          needsSync = true;
        }
      });
      
      if (needsSync) {
        setTimeout(() => {
          this.forceSyncFieldOptions(calculatorField, wizardField, fieldName);
        }, 100);
      }
    });
    
    observer.observe(calculatorField, {
      attributes: true,
      childList: true,
      subtree: true
    });
    
    setTimeout(() => {
      this.forceSyncFieldOptions(calculatorField, wizardField, fieldName);
    }, 200);
    
    this.fieldObservers.set(fieldName, observer);
  }

  forceSyncFieldOptions(calculatorField, wizardField, fieldName) {
    try {
      if (calculatorField.options.length <= 1) {
        return;
      }
      
      const currentValue = wizardField.value;
      const calcValue = calculatorField.value;
      
      const wizardOptions = Array.from(wizardField.options).map(o => o.value).join('|');
      const calcOptions = Array.from(calculatorField.options).map(o => o.value).join('|');
      
      if (wizardOptions !== calcOptions) {
        wizardField.innerHTML = '';
        
        for (let i = 0; i < calculatorField.options.length; i++) {
          const originalOption = calculatorField.options[i];
          const newOption = document.createElement('option');
          newOption.value = originalOption.value;
          newOption.textContent = originalOption.textContent;
          newOption.disabled = originalOption.disabled;
          newOption.hidden = originalOption.hidden;
          
          Array.from(originalOption.attributes).forEach(attr => {
            if (attr.name.startsWith('data-')) {
              newOption.setAttribute(attr.name, attr.value);
            }
          });
          
          wizardField.appendChild(newOption);
        }
        
        wizardField.value = calcValue || currentValue;
      }
      
      wizardField.disabled = calculatorField.disabled;
      
      // Sync container CSS class with disabled state
      const fieldContainer = wizardField.closest('.ht-wizard-field');
      if (fieldContainer) {
        if (calculatorField.disabled) {
          fieldContainer.classList.add('ht-wizard-field--disabled');
        } else {
          fieldContainer.classList.remove('ht-wizard-field--disabled');
          const note = fieldContainer.querySelector('.ht-wizard-field-note--disabled');
          if (note) note.remove();
        }
      }
      
      this.updateFieldCompletionState(wizardField);
      
    } catch (error) {
      // Silent error handling
    }
  }

  syncRegularField(calculatorField, wizardField, fieldName) {
    wizardField.value = calculatorField.value;
    
    if (['quantity', 'print_sides', 'print_ink'].includes(fieldName)) {
      this.preservedValues[fieldName] = calculatorField.value;
    }
    
    wizardField.addEventListener('change', (e) => {
      if (e.target.disabled) {
        return;
      }
      
      if (['quantity', 'print_sides', 'print_ink'].includes(fieldName)) {
        this.preservedValues[fieldName] = e.target.value;
      }
      
      calculatorField.value = e.target.value;
      
      setTimeout(() => {
        this.handleCustomFieldTriggers(fieldName, e.target.value);
      }, 10);
      
      // Ensure change event fires properly
      calculatorField.dispatchEvent(new Event('change', { bubbles: true }));
      
      // For styled calculator, force immediate calculation and validation
      if (this.calculatorType === 'styled') {
        setTimeout(() => {
          this.calculator.calculatePriceIfReady();
          
          // Force immediate navigation state update after field changes
          setTimeout(() => {
            this.updateNavigationState();
          }, 50);
        }, 50);
      }
      
      // Enhanced synchronization for style_template in styled calculator
      if (fieldName === 'style_template' && this.calculatorType === 'styled') {
        setTimeout(() => {
          this.reconfigureWizardSteps();
        }, 10);
        
        // Immediate sync using requestAnimationFrame
        requestAnimationFrame(() => {
          this.syncStyledCalculatorFieldStates();
        });
        
        // Quick follow-up sync
        setTimeout(() => {
          this.syncStyledCalculatorFieldStates();
        }, 50);
        
        // Secondary sync for safety
        setTimeout(() => {
          this.syncStyledCalculatorFieldStates();
        }, 200);
        
        // Final sync to catch any delayed updates
        setTimeout(() => {
          this.syncStyledCalculatorFieldStates();
        }, 400);
      }
      
      // Enhanced synchronization for base changes in styled calculator
      if (fieldName === 'base' && this.calculatorType === 'styled') {
        // Immediate sync using requestAnimationFrame
        requestAnimationFrame(() => {
          this.syncStyledCalculatorFieldStates();
        });
        
        // Quick follow-up sync
        setTimeout(() => {
          this.syncStyledCalculatorFieldStates();
        }, 50);
        
        // Final sync to ensure material dropdown is enabled
        setTimeout(() => {
          this.syncStyledCalculatorFieldStates();
        }, 150);
      }

      if (fieldName === 'material') {
        setTimeout(() => {
          this.updatePrintFieldLabels();
        }, 100);

        // Re-sync styled calculator field states when material changes
        if (this.calculatorType === 'styled') {
          setTimeout(() => {
            this.syncStyledCalculatorFieldStates();
          }, 150);
        }
      }
      
      this.queueFieldUpdate(fieldName, () => {
        this.updateWizardSummary();
        this.updateFieldCompletionState(wizardField);
      });
    });
    
    const observer = new MutationObserver((mutations) => {
      if (!this.initialLoadComplete && mutations.length > 3) {
        return;
      }
      
      this.queueFieldUpdate(fieldName, () => {
        this.handleFieldMutations(mutations, calculatorField, wizardField, fieldName);
        
        // Additional sync for styled calculator field state changes
        if (this.calculatorType === 'styled' && fieldName === 'style_template') {
          setTimeout(() => {
            this.syncStyledCalculatorFieldStates();
          }, 100);
        }
      });
    });
    
    observer.observe(calculatorField, {
      attributes: true,
      childList: true,
      subtree: false
    });
    
    this.fieldObservers.set(fieldName, observer);
  }

  handleFieldMutations(mutations, calculatorField, wizardField, fieldName) {
    mutations.forEach(mutation => {
      if (mutation.type === 'attributes') {
        if (mutation.attributeName === 'disabled') {
          wizardField.disabled = calculatorField.disabled;
          
          const fieldContainer = wizardField.closest('.ht-wizard-field');
          if (fieldContainer) {
            if (calculatorField.disabled) {
              fieldContainer.classList.add('ht-wizard-field--disabled');
              let note = fieldContainer.querySelector('.ht-wizard-field-note--disabled');
              if (!note) {
                note = document.createElement('div');
                note.className = 'ht-wizard-field-note ht-wizard-field-note--disabled';
                note.textContent = 'Not available for selected template';
                fieldContainer.appendChild(note);
              }
            } else {
              fieldContainer.classList.remove('ht-wizard-field--disabled');
              const note = fieldContainer.querySelector('.ht-wizard-field-note--disabled');
              if (note) {
                note.remove();
              }
            }
          }
          
          // Special handling for size field when custom shape/style
          if (fieldName === 'size') {
            this.handleSizeFieldVisibility();
          }
          
          setTimeout(() => {
            this.updateNavigationState();
          }, 50);
        }
      } else if (mutation.type === 'childList') {
        this.updateFieldOptions(calculatorField, wizardField, fieldName);
      }
    });
  }

  updateFieldOptions(calculatorField, wizardField, fieldName) {
    const currentValue = wizardField.value;
    const currentCalcValue = calculatorField.value;
    
    const wizardOptions = Array.from(wizardField.options).map(o => o.value).join(',');
    const calcOptions = Array.from(calculatorField.options).map(o => o.value).join(',');
    
    if (wizardOptions !== calcOptions) {
      const documentFragment = document.createDocumentFragment();
      
      for (let i = 0; i < calculatorField.options.length; i++) {
        const option = calculatorField.options[i].cloneNode(true);
        documentFragment.appendChild(option);
      }
      
      wizardField.innerHTML = '';
      wizardField.appendChild(documentFragment);
      
      if (this.preservedValues[fieldName]) {
        const isValidOption = Array.from(calculatorField.options).some(opt => opt.value === this.preservedValues[fieldName]);
        if (isValidOption) {
          wizardField.value = this.preservedValues[fieldName];
          calculatorField.value = this.preservedValues[fieldName];
          calculatorField.dispatchEvent(new Event('change'));
        } else {
          wizardField.value = currentCalcValue || currentValue;
        }
      } else {
        wizardField.value = currentCalcValue || currentValue;
      }
    } else {
      if (wizardField.value !== calculatorField.value) {
        wizardField.value = calculatorField.value;
      }
    }
    
    this.updateFieldCompletionState(wizardField);
  }

  updateFieldCompletionState(wizardField) {
    const fieldContainer = wizardField.closest('.ht-wizard-field');
    
    if (fieldContainer) {
      if (wizardField.value && wizardField.value !== '') {
        fieldContainer.classList.add('ht-wizard-field--completed');
      } else {
        fieldContainer.classList.remove('ht-wizard-field--completed');
      }
    }
  }

  handleSizeFieldVisibility() {
    const shapeValue = this.calculator?.shapeSelect?.value;
    const sizeFieldContainer = document.getElementById('ht-wizard-field-size');
    
    if (shapeValue === 'custom' && sizeFieldContainer) {
      sizeFieldContainer.style.display = 'none';
    } else if (sizeFieldContainer) {
      sizeFieldContainer.style.display = 'block';
    }
  }

  handleCustomFieldTriggers(fieldName, value) {
    if (!this.calculator) return;
    
    if (!this.initialLoadComplete) {
      return;
    }
    
    switch (fieldName) {
      case 'material':
        break;
      case 'shape':
      case 'style_template':
        this.handleShapeChange(value);
        // Force sync for styled calculator after style template change
        if (this.calculatorType === 'styled' && fieldName === 'style_template') {
          setTimeout(() => {
            this.syncStyledCalculatorFieldStates();
          }, 150);
        }
        break;
      case 'size':
        this.handleSizeChange(value);
        break;
      case 'quantity':
        this.handleQuantityChange(value);
        break;
      case 'drill_diameter':
        this.handleDrillDiameterChange(value);
        break;
      case 'string_attachment':
        this.handleStringAttachmentChange(value);
        break;
      case 'eyelet':
        this.handleEyeletChange(value);
        break;
    }
  }

  handleDrillDiameterChange(value) {
    // Use ID selector for the field container
    const wizardDrillPosField = document.getElementById('ht-wizard-field-drill_position');
    const wizardDrillPosSelect = document.getElementById('ht-wizard-drill_position');
    const calcDrillPos = this.getCalculatorField('drill_position');
    
    if (value && value !== '' && value !== '0' && value !== 'None') {
      // Drill diameter selected - show and enable position field
      if (wizardDrillPosField) {
        wizardDrillPosField.style.display = 'block';
        wizardDrillPosField.classList.remove('ht-wizard-field--hidden');
      }
      
      if (wizardDrillPosSelect && calcDrillPos) {
        // First, sync the options from calculator to wizard
        wizardDrillPosSelect.innerHTML = '';
        for (let i = 0; i < calcDrillPos.options.length; i++) {
          const option = calcDrillPos.options[i].cloneNode(true);
          wizardDrillPosSelect.appendChild(option);
        }
        
        wizardDrillPosSelect.disabled = false;
        
        // AUTO-SELECT FIRST VALID OPTION
        setTimeout(() => {
          if (!wizardDrillPosSelect.value || wizardDrillPosSelect.value === '') {
            // Find first non-empty option
            for (let i = 0; i < wizardDrillPosSelect.options.length; i++) {
              const option = wizardDrillPosSelect.options[i];
              if (option.value && option.value !== '' && option.value !== 'None') {
                // Set wizard field value
                wizardDrillPosSelect.value = option.value;
                
                // Set calculator field value and trigger change
                calcDrillPos.value = option.value;
                
                // Create and dispatch a proper change event
                const changeEvent = new Event('change', { 
                  bubbles: true, 
                  cancelable: true 
                });
                calcDrillPos.dispatchEvent(changeEvent);
                
                // Also trigger input event for good measure
                const inputEvent = new Event('input', { 
                  bubbles: true, 
                  cancelable: true 
                });
                calcDrillPos.dispatchEvent(inputEvent);
                
                // Force update of wizard field state
                this.updateFieldCompletionState(wizardDrillPosSelect);
                break;
              }
            }
          }
          
          // Add required indicator
          const label = wizardDrillPosField?.querySelector('.ht-wizard-field-label');
          if (label && !label.classList.contains('ht-wizard-field-required')) {
            label.classList.add('ht-wizard-field-required');
          }
          
          // Update navigation state after selection
          this.updateNavigationState();
          
          // Additional delay for price calculation to ensure all events have processed
          setTimeout(() => {
            if (this.calculator && this.calculator.calculatePriceIfReady) {
              this.calculator.calculatePriceIfReady();
            }
          }, 100);
        }, 50);
      }
    } else {
      // No drill diameter or "None" selected - hide and reset position field
      if (wizardDrillPosField) {
        wizardDrillPosField.style.display = 'none';
      }
      
      if (wizardDrillPosSelect) {
        wizardDrillPosSelect.value = '';
        wizardDrillPosSelect.disabled = true;
        this.updateFieldCompletionState(wizardDrillPosSelect);
        
        // Also clear the calculator field
        if (calcDrillPos) {
          calcDrillPos.value = '';
          calcDrillPos.dispatchEvent(new Event('change'));
        }
        
        // Remove required indicator
        const label = wizardDrillPosField?.querySelector('.ht-wizard-field-label');
        if (label) {
          label.classList.remove('ht-wizard-field-required');
        }
      }
      
      // Update navigation state
      this.updateNavigationState();
    }
  }

  syncDrillPositionVisibility() {
    const drillDiameterField = this.getCalculatorField('drill_diameter');
    if (drillDiameterField && drillDiameterField.value && drillDiameterField.value !== '' && drillDiameterField.value !== '0') {
      this.handleDrillDiameterChange(drillDiameterField.value);
    }
  }

  handleShapeChange(value) {
    const customDimensionsContainer = document.getElementById('ht-wizard-custom-dimensions-container');
    const sizeFieldContainer = document.getElementById('ht-wizard-field-size');
    
    const preserveQuantity = this.calculator.quantitySelect.value;
    const preservePrintSides = this.calculator.printSidesSelect.value;
    const preservePrintInk = this.calculator.printInkSelect.value;
    
    if (value === 'custom') {
      if (this.calculator.showCustomSizeFields) {
        this.calculator.showCustomSizeFields('custom_shape');
      }
      if (customDimensionsContainer) {
        customDimensionsContainer.style.display = 'block';
      }
      if (sizeFieldContainer) {
        sizeFieldContainer.style.display = 'none';
      }
    } else {
      if (customDimensionsContainer) {
        customDimensionsContainer.style.display = 'none';
      }
      if (sizeFieldContainer) {
        sizeFieldContainer.style.display = 'block';
      }
      
      if (this.calculator.isCustomSizeActive && this.calculator.customSizeReason === 'custom_shape') {
        if (this.calculator.hideCustomSizeFields) {
          this.calculator.hideCustomSizeFields();
        }
      }
    }
    
    setTimeout(() => {
      this.restorePreservedValues(preserveQuantity, preservePrintSides, preservePrintInk);
      this.fixQuantityFieldState();
    }, 100);
    
    setTimeout(() => {
      this.ensureQuantitySync(preserveQuantity);
    }, 200);
  }

  handleSizeChange(value) {
    const customDimContainer = document.getElementById('ht-wizard-custom-dimensions-container');
    
    if (value === 'custom_size') {
      if (this.calculator.showCustomSizeFields) {
        this.calculator.showCustomSizeFields('custom_size_option');
      }
      if (customDimContainer) {
        customDimContainer.style.display = 'block';
      }
      
      return;
    } else if (value && value !== '') {
      if (this.calculator.hideCustomSizeFields) {
        this.calculator.hideCustomSizeFields();
      }
      if (customDimContainer) {
        customDimContainer.style.display = 'none';
      }

      setTimeout(() => {
        this.updateNavigationState();
      }, 100);
    } else {
      if (this.calculator.hideCustomSizeFields) {
        this.calculator.hideCustomSizeFields();
      }
      if (customDimContainer) {
        customDimContainer.style.display = 'none';
      }
      
      setTimeout(() => {
        this.updateNavigationState();
      }, 100);
    }
  }

  handleQuantityChange(value) {
    const wizardCustomContainer = document.getElementById('ht-wizard-custom-quantity-container');
    const wizardCustomInput = document.getElementById('ht-wizard-custom-quantity');
    
    if (value === 'custom') {
      if (this.calculator.showCustomQuantityField) {
        this.calculator.showCustomQuantityField();
      }
      if (wizardCustomContainer) {
        wizardCustomContainer.style.display = 'block';
      }
      if (wizardCustomInput) {
        wizardCustomInput.disabled = false;
        wizardCustomInput.focus();
      }
      setTimeout(() => {
        this.syncCustomQuantity();
      }, 50);
    } else {
      if (this.calculator.hideCustomQuantityField) {
        this.calculator.hideCustomQuantityField();
      }
      
      // Hide wizard's custom field
      if (wizardCustomContainer) {
        wizardCustomContainer.style.display = 'none';
      }
      if (wizardCustomInput) {
        wizardCustomInput.value = '';
        wizardCustomInput.disabled = true;
      }
    }
  }

  // Single source of truth for "string is being supplied".
  // False when value is empty/null OR resolves to a "none" option
  // (e.g. string_attachment_none); true otherwise.
  isStringSupplied(value) {
    if (!value || value === '') {
      return false;
    }
    if (String(value).toLowerCase().includes('none')) {
      return false;
    }
    return true;
  }

  handleStringAttachmentChange(value) {
    const stringTypeField = document.getElementById('ht-wizard-field-string_type');
    const supplied = this.isStringSupplied(value);

    if (stringTypeField) {
      stringTypeField.style.display = supplied ? 'block' : 'none';

      // Toggle the required-asterisk on the String Type label,
      // mirroring the drill-position required-field handling.
      const label = stringTypeField.querySelector('.ht-wizard-field-label');
      if (label) {
        if (supplied) {
          label.classList.add('ht-wizard-field-required');
        } else {
          label.classList.remove('ht-wizard-field-required');
        }
      }
    }

    this.updateNavigationState();
  }

  // Re-apply String Type visibility + required state when Step 2 is
  // (re)rendered or revisited, mirroring syncDrillPositionVisibility().
  syncStringTypeVisibility() {
    const stringAttachmentField = this.getCalculatorField('string_attachment');
    if (stringAttachmentField) {
      this.handleStringAttachmentChange(stringAttachmentField.value);
    }
  }

  // Handle eyelet selection - auto-select 4mm drill hole when eyelet is chosen
  // For round/oval shapes, also manage 4mm visibility in dropdown
  handleEyeletChange(value) {
    // Check if an actual eyelet is selected (not None or empty)
    const isEyeletSelected = value &&
                             value !== '' &&
                             value !== 'eyelet_none' &&
                             !value.toLowerCase().includes('none');

    // Update drill diameter note based on eyelet selection
    this.updateEyeletNote(isEyeletSelected);

    // Get drill diameter fields
    const wizardDrillDiameterSelect = document.getElementById('ht-wizard-drill_diameter');
    const calcDrillDiameter = this.getCalculatorField('drill_diameter');

    if (!wizardDrillDiameterSelect || !calcDrillDiameter) {
      return;
    }

    // Check if this is a round/oval shape (4mm should be hidden unless eyelet selected)
    const isRoundOrOval = this.calculator && this.calculator.isRoundOrOvalShape && this.calculator.isRoundOrOvalShape();

    // For round/oval shapes, refresh drill diameters to show/hide 4mm
    if (isRoundOrOval && this.calculator && this.calculator.refreshDrillDiameters) {
      this.calculator.refreshDrillDiameters();
      // Sync wizard dropdown with calculator after refresh
      this.syncWizardDrillDiametersFromCalculator();
    }

    if (!isEyeletSelected) {
      // If eyelet deselected on round/oval, reset drill hole to 3mm or None
      if (isRoundOrOval) {
        const currentValue = calcDrillDiameter.value;
        if (currentValue === '4') {
          // Find 3mm option or fall back to None
          let newValue = '0';
          for (let i = 0; i < calcDrillDiameter.options.length; i++) {
            if (calcDrillDiameter.options[i].value === '3') {
              newValue = '3';
              break;
            }
          }
          calcDrillDiameter.value = newValue;
          wizardDrillDiameterSelect.value = newValue;

          // Dispatch change event to trigger calculator update
          const changeEvent = new Event('change', { bubbles: true, cancelable: true });
          calcDrillDiameter.dispatchEvent(changeEvent);

          this.updateFieldCompletionState(wizardDrillDiameterSelect);
          this.handleDrillDiameterChange(newValue);
        }
      }
      return;
    }

    // Set drill diameter to 4mm (override any existing selection)
    const targetDiameter = '4';

    // Check if 4mm option exists in the dropdown
    let found4mm = false;
    for (let i = 0; i < calcDrillDiameter.options.length; i++) {
      if (calcDrillDiameter.options[i].value === targetDiameter) {
        found4mm = true;
        break;
      }
    }

    if (!found4mm) {
      return;
    }

    // Set the calculator field value
    calcDrillDiameter.value = targetDiameter;

    // Dispatch change event to trigger calculator update
    const changeEvent = new Event('change', { bubbles: true, cancelable: true });
    calcDrillDiameter.dispatchEvent(changeEvent);

    // Sync wizard field
    wizardDrillDiameterSelect.value = targetDiameter;
    this.updateFieldCompletionState(wizardDrillDiameterSelect);

    // Trigger drill diameter change handler to show position field
    setTimeout(() => {
      this.handleDrillDiameterChange(targetDiameter);
    }, 50);
  }

  // Sync wizard drill diameter dropdown from calculator
  syncWizardDrillDiametersFromCalculator() {
    const wizardDrillDiameterSelect = document.getElementById('ht-wizard-drill_diameter');
    const calcDrillDiameter = this.getCalculatorField('drill_diameter');

    if (!wizardDrillDiameterSelect || !calcDrillDiameter) return;

    const currentValue = wizardDrillDiameterSelect.value;

    // Copy options from calculator to wizard
    wizardDrillDiameterSelect.innerHTML = '';
    for (let i = 0; i < calcDrillDiameter.options.length; i++) {
      const option = calcDrillDiameter.options[i].cloneNode(true);
      wizardDrillDiameterSelect.appendChild(option);
    }

    // Try to restore previous value if still valid
    let valueRestored = false;
    for (let i = 0; i < wizardDrillDiameterSelect.options.length; i++) {
      if (wizardDrillDiameterSelect.options[i].value === currentValue) {
        wizardDrillDiameterSelect.value = currentValue;
        valueRestored = true;
        break;
      }
    }

    // If previous value not found, sync with calculator's current value
    if (!valueRestored) {
      wizardDrillDiameterSelect.value = calcDrillDiameter.value;
    }

    this.updateFieldCompletionState(wizardDrillDiameterSelect);
  }

  restorePreservedValues(quantity, printSides, printInk) {
    if (quantity && this.calculator.quantitySelect) {
      this.calculator.quantitySelect.value = quantity;
      const wizardQuantity = document.getElementById('ht-wizard-quantity');
      if (wizardQuantity) {
        wizardQuantity.value = quantity;
        this.updateFieldCompletionState(wizardQuantity);
      }
    }
    if (printSides && this.calculator.printSidesSelect) {
      this.calculator.printSidesSelect.value = printSides;
      const wizardPrintSides = document.getElementById('ht-wizard-print_sides');
      if (wizardPrintSides) {
        wizardPrintSides.value = printSides;
        this.updateFieldCompletionState(wizardPrintSides);
      }
    }
    if (printInk && this.calculator.printInkSelect) {
      this.calculator.printInkSelect.value = printInk;
      const wizardPrintInk = document.getElementById('ht-wizard-print_ink');
      if (wizardPrintInk) {
        wizardPrintInk.value = printInk;
        this.updateFieldCompletionState(wizardPrintInk);
      }
    }
    
    this.updateNavigationState();
    this.updateWizardSummary();
  }
  
  fixQuantityFieldState() {
    const calcQuantity = this.calculator?.quantitySelect;
    const wizardQuantity = document.getElementById('ht-wizard-quantity');
    
    if (calcQuantity && wizardQuantity) {
      wizardQuantity.disabled = calcQuantity.disabled;
      wizardQuantity.value = calcQuantity.value;
      
      this.updateFieldCompletionState(wizardQuantity);
      
      const fieldContainer = wizardQuantity.closest('.ht-wizard-field');
      if (fieldContainer) {
        fieldContainer.classList.remove('ht-wizard-field--disabled', 'ht-wizard-field--completed');
        
        if (wizardQuantity.disabled) {
          fieldContainer.classList.add('ht-wizard-field--disabled');
        }
        if (wizardQuantity.value && wizardQuantity.value !== '') {
          fieldContainer.classList.add('ht-wizard-field--completed');
        }
      }
    }
  }
  
  ensureQuantitySync(expectedQuantity) {
    const calcQuantity = this.calculator?.quantitySelect;
    const wizardQuantity = document.getElementById('ht-wizard-quantity');
    
    if (calcQuantity && wizardQuantity && expectedQuantity) {
      if (calcQuantity.value !== expectedQuantity || wizardQuantity.value !== expectedQuantity) {
        calcQuantity.value = expectedQuantity;
        wizardQuantity.value = expectedQuantity;
        
        calcQuantity.dispatchEvent(new Event('change'));
        
        this.updateFieldCompletionState(wizardQuantity);
      }
    }
  }
  
  cleanupObservers() {
    this.fieldObservers.forEach(observer => {
      observer.disconnect();
    });
    this.fieldObservers.clear();
  }

  syncCustomDimensions() {
    const wizardWidthInput = document.getElementById('ht-wizard-custom-width');
    const wizardHeightInput = document.getElementById('ht-wizard-custom-height');
    const calcWidthInput = document.getElementById('ht-custom-width');
    const calcHeightInput = document.getElementById('ht-custom-height');
    
    if (!wizardWidthInput || !wizardHeightInput || !calcWidthInput || !calcHeightInput) {
      return;
    }
    
    wizardWidthInput.value = calcWidthInput.value;
    wizardHeightInput.value = calcHeightInput.value;
    
    wizardWidthInput.addEventListener('input', (e) => {
      calcWidthInput.value = e.target.value;
      calcWidthInput.dispatchEvent(new Event('input'));
      this.validateCustomDimensionsDisplay();
      this.updateWizardSummary();
      this.updateNavigationState();
    });
    
    wizardHeightInput.addEventListener('input', (e) => {
      calcHeightInput.value = e.target.value;
      calcHeightInput.dispatchEvent(new Event('input'));
      this.validateCustomDimensionsDisplay();
      this.updateWizardSummary();
      this.updateNavigationState();
    });
    
    const widthObserver = new MutationObserver(() => {
      if (wizardWidthInput.value !== calcWidthInput.value) {
        wizardWidthInput.value = calcWidthInput.value;
        this.validateCustomDimensionsDisplay();
      }
      wizardWidthInput.min = calcWidthInput.min;
      wizardWidthInput.max = calcWidthInput.max;
      
      const widthConstraint = wizardWidthInput.parentElement.querySelector('.ht-wizard-field-constraint');
      if (widthConstraint) {
        widthConstraint.textContent = `Min: ${calcWidthInput.min}mm, Max: ${calcWidthInput.max}mm`;
      }
    });
    
    const heightObserver = new MutationObserver(() => {
      if (wizardHeightInput.value !== calcHeightInput.value) {
        wizardHeightInput.value = calcHeightInput.value;
        this.validateCustomDimensionsDisplay();
      }
      wizardHeightInput.min = calcHeightInput.min;
      wizardHeightInput.max = calcHeightInput.max;
      
      const heightConstraint = wizardHeightInput.parentElement.querySelector('.ht-wizard-field-constraint');
      if (heightConstraint) {
        heightConstraint.textContent = `Min: ${calcHeightInput.min}mm, Max: ${calcHeightInput.max}mm`;
      }
    });
    
    widthObserver.observe(calcWidthInput, {
      attributes: true,
      attributeFilter: ['value', 'min', 'max']
    });
    
    heightObserver.observe(calcHeightInput, {
      attributes: true,
      attributeFilter: ['value', 'min', 'max']
    });
    
    this.fieldObservers.set('custom_width', widthObserver);
    this.fieldObservers.set('custom_height', heightObserver);
  }
  
  validateCustomDimensionsDisplay() {
    if (!this.calculator) return;
    
    const validationEl = document.getElementById('ht-wizard-custom-dimensions-validation');
    if (!validationEl) return;
    
    const isValid = this.calculator.validateCustomSize();
    
    if (!isValid && this.calculator.sizeValidation) {
      validationEl.innerHTML = this.calculator.sizeValidation.innerHTML;
      validationEl.style.display = 'block';
      validationEl.style.color = '#d73527';
    } else {
      validationEl.style.display = 'none';
    }
  }

  syncStyledCalculatorFieldStates() {
    if (this.calculatorType !== 'styled') return;
    
    requestAnimationFrame(() => {
      // Get all calculator fields directly from DOM
      const styleTemplateCalcField = document.getElementById('ht-shape-select');
      const materialCalcField = document.getElementById('ht-material-select');
      const quantityCalcField = document.getElementById('ht-quantity-select');
      const printSidesCalcField = document.getElementById('ht-print-sides-select');
      const printInkCalcField = document.getElementById('ht-print-ink-select');
      
      // Get wizard fields
      const materialWizardField = document.getElementById('ht-wizard-material');
      const quantityWizardField = document.getElementById('ht-wizard-quantity');
      const printSidesWizardField = document.getElementById('ht-wizard-print_sides');
      const printInkWizardField = document.getElementById('ht-wizard-print_ink');
      
      // Check what's selected
      const hasStyleTemplate = styleTemplateCalcField && styleTemplateCalcField.value && styleTemplateCalcField.value !== '';
      const hasMaterial = materialCalcField && materialCalcField.value && materialCalcField.value !== '';
      const hasQuantity = quantityCalcField && quantityCalcField.value && quantityCalcField.value !== '';
      
      // MATERIAL FIELD SYNC
      if (materialCalcField && materialWizardField) {
        const baseCalcField = document.getElementById('ht-base-select');
        const hasBase = baseCalcField && !baseCalcField.disabled && baseCalcField.value && baseCalcField.value !== '';
        const shouldMaterialBeEnabled = hasStyleTemplate && hasBase;
        
        materialWizardField.disabled = !shouldMaterialBeEnabled;
        
        const fieldContainer = materialWizardField.closest('.ht-wizard-field');
        if (fieldContainer) {
          if (shouldMaterialBeEnabled) {
            fieldContainer.classList.remove('ht-wizard-field--disabled');
            const note = fieldContainer.querySelector('.ht-wizard-field-note--disabled');
            if (note) note.remove();
          } else {
            fieldContainer.classList.add('ht-wizard-field--disabled');
          }
        }
        
        this.updateFieldCompletionState(materialWizardField);
      }
      
      // QUANTITY FIELD SYNC
      if (quantityCalcField && quantityWizardField) {
        const isQuantityEnabledInCalc = !quantityCalcField.disabled;
        const shouldQuantityBeEnabled = hasMaterial && isQuantityEnabledInCalc;
        
        quantityWizardField.disabled = !shouldQuantityBeEnabled;
        
        const fieldContainer = quantityWizardField.closest('.ht-wizard-field');
        if (fieldContainer) {
          if (shouldQuantityBeEnabled) {
            fieldContainer.classList.remove('ht-wizard-field--disabled');
            const note = fieldContainer.querySelector('.ht-wizard-field-note--disabled');
            if (note) note.remove();
          } else {
            fieldContainer.classList.add('ht-wizard-field--disabled');
          }
        }
        
        this.updateFieldCompletionState(quantityWizardField);
      }
      
      // PRINT SIDES FIELD SYNC
      if (printSidesCalcField && printSidesWizardField) {
        const isPrintSidesEnabledInCalc = !printSidesCalcField.disabled;
        const shouldPrintSidesBeEnabled = hasQuantity && isPrintSidesEnabledInCalc;
        
        printSidesWizardField.disabled = !shouldPrintSidesBeEnabled;
        
        const fieldContainer = printSidesWizardField.closest('.ht-wizard-field');
        if (fieldContainer) {
          if (shouldPrintSidesBeEnabled) {
            fieldContainer.classList.remove('ht-wizard-field--disabled');
            const note = fieldContainer.querySelector('.ht-wizard-field-note--disabled');
            if (note) note.remove();
          } else {
            fieldContainer.classList.add('ht-wizard-field--disabled');
          }
        }
        
        this.updateFieldCompletionState(printSidesWizardField);
      }
      
      // PRINT INK FIELD SYNC
      if (printInkCalcField && printInkWizardField) {
        const isPrintInkEnabledInCalc = !printInkCalcField.disabled;
        const shouldPrintInkBeEnabled = hasQuantity && isPrintInkEnabledInCalc;
        
        printInkWizardField.disabled = !shouldPrintInkBeEnabled;
        
        const fieldContainer = printInkWizardField.closest('.ht-wizard-field');
        if (fieldContainer) {
          if (shouldPrintInkBeEnabled) {
            fieldContainer.classList.remove('ht-wizard-field--disabled');
            const note = fieldContainer.querySelector('.ht-wizard-field-note--disabled');
            if (note) note.remove();
          } else {
            fieldContainer.classList.add('ht-wizard-field--disabled');
          }
        }
        
        this.updateFieldCompletionState(printInkWizardField);
      }
      
      this.updateNavigationState();
    });
  }

  syncPreselectedValues() {
    // Check if state manager exists and has getCachedState method
    if (window.htStateManager && typeof window.htStateManager.getCachedState === 'function') {
      try {
        const state = window.htStateManager.getCachedState();
        // Check if state is valid (has timestamp and calculatorType)
        if (state && state.timestamp && state.calculatorType === 'styled') {
          // Sync shape/style template FIRST
          if (state.shape && this.calculator.shapeSelect) {
            this.calculator.shapeSelect.value = state.shape;
            this.calculator.shapeSelect.dispatchEvent(new Event('change'));
            
            // Force wizard field sync for style_template
            const wizardStyleField = document.getElementById('ht-wizard-style_template');
            if (wizardStyleField) {
              wizardStyleField.value = state.shape;
              this.updateFieldCompletionState(wizardStyleField);
            }
          }
          
          // Sync base selection
          if (state.base && this.calculator.baseSelect) {
            this.calculator.baseSelect.value = state.base;
            this.calculator.baseSelect.dispatchEvent(new Event('change'));
            
            // Force wizard field sync for base
            const wizardBaseField = document.getElementById('ht-wizard-base');
            if (wizardBaseField) {
              wizardBaseField.value = state.base;
              this.updateFieldCompletionState(wizardBaseField);
            }
          }
          
          // Sync material selection
          if (state.material && this.calculator.materialSelect) {
            this.calculator.materialSelect.value = state.material;
            this.calculator.materialSelect.dispatchEvent(new Event('change'));
            
            // Force wizard field sync for material
            const wizardMaterialField = document.getElementById('ht-wizard-material');
            if (wizardMaterialField) {
              wizardMaterialField.value = state.material;
              this.updateFieldCompletionState(wizardMaterialField);
            }
          }
          
          // Sync quantity if available
          if (state.quantity && this.calculator.quantitySelect) {
            const qtyStr = String(state.quantity);
            const hasOption = Array.from(this.calculator.quantitySelect.options)
              .some(opt => opt.value === qtyStr);

            if (hasOption) {
              this.calculator.quantitySelect.value = qtyStr;
            } else {
              // Custom-typed number — preserve via 'custom' + custom input
              this.calculator.quantitySelect.value = 'custom';
              if (this.calculator.customQuantityInput) {
                this.calculator.customQuantityInput.value = qtyStr;
              }
            }
            this.calculator.quantitySelect.dispatchEvent(new Event('change'));

            // Force wizard field sync for quantity
            const wizardQuantityField = document.getElementById('ht-wizard-quantity');
            if (wizardQuantityField) {
              wizardQuantityField.value = this.calculator.quantitySelect.value;
              this.updateFieldCompletionState(wizardQuantityField);
            }
          }
          
          // Force immediate wizard field states sync
          setTimeout(() => {
            this.syncStyledCalculatorFieldStates();
            
            // After sync, force completion state check on ALL fields
            ['style_template', 'base', 'material', 'quantity', 'print_sides', 'print_ink'].forEach(fieldName => {
              const wizardField = document.getElementById(`ht-wizard-${fieldName}`);
              if (wizardField && wizardField.value && wizardField.value !== '') {
                this.updateFieldCompletionState(wizardField);
              }
            });
          }, 100);
        }
      } catch (error) {
        // Silent error handling
      }
    }
  }

  forceStyledCalculatorSync() {
    if (this.calculatorType !== 'styled') return;
    
    // Get current calculator field states
    const materialCalcField = this.getCalculatorField('material');
    const quantityCalcField = this.getCalculatorField('quantity');
    const printSidesCalcField = this.getCalculatorField('print_sides');
    const printInkCalcField = this.getCalculatorField('print_ink');
    
    // Update wizard fields to match calculator immediately
    if (materialCalcField) {
      const materialWizardField = document.getElementById('ht-wizard-material');
      if (materialWizardField) {
        const shouldBeEnabled = !materialCalcField.disabled;
        materialWizardField.disabled = !shouldBeEnabled;
        
        const fieldContainer = materialWizardField.closest('.ht-wizard-field');
        if (fieldContainer) {
          if (shouldBeEnabled) {
            fieldContainer.classList.remove('ht-wizard-field--disabled');
            const note = fieldContainer.querySelector('.ht-wizard-field-note--disabled');
            if (note) note.remove();
          } else {
            fieldContainer.classList.add('ht-wizard-field--disabled');
          }
        }
      }
    }
    
    // Repeat for other fields
    if (quantityCalcField) {
      const quantityWizardField = document.getElementById('ht-wizard-quantity');
      if (quantityWizardField) {
        const shouldBeEnabled = !quantityCalcField.disabled;
        quantityWizardField.disabled = !shouldBeEnabled;
      }
    }
  }

  bindWizardEvents() {
    this.updateStepDisplay();
    
    const backBtn = document.getElementById('ht-wizard-back');
    const nextBtn = document.getElementById('ht-wizard-next');
    
    if (backBtn) {
      backBtn.addEventListener('click', () => this.goToPreviousStep());
    }
    
    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToNextStep();
      });
    }
    
    this.hookCalculatorEvents();
  }
  
  hookCalculatorEvents() {
    if (!this.calculator) return;
    
    const originalCalculatePrice = this.calculator.calculatePriceIfReady.bind(this.calculator);
    this.calculator.calculatePriceIfReady = () => {
      originalCalculatePrice();
      this.updateWizardSummary();
      this.updateNavigationState();
    };
    
    // Hook the actual calculatePrice method instead of updatePriceDisplay
    if (this.calculatorType === 'styled' && this.calculator.calculatePrice) {
      const originalCalcPrice = this.calculator.calculatePrice.bind(this.calculator);
      this.calculator.calculatePrice = () => {
        // Call original calculation
        originalCalcPrice();
        
        // Extract price from the calculator's display elements immediately after calculation
        setTimeout(() => {
          const totalPriceElement = this.calculator.totalPriceDisplay;
          if (totalPriceElement && totalPriceElement.textContent) {
            const priceText = totalPriceElement.textContent;
            // Remove both £ and ALL commas globally for correct parsing
            const priceValue = parseFloat(priceText.replace(/[£,]/g, '')) || 0;
            
            if (priceValue > 0) {
              this.updateWizardPriceDirectly(priceValue, 0);
            }
          }
        }, 10);
      };
    }
    
    // BACKUP: Also hook updatePriceDisplay as secondary catch
    if (this.calculatorType === 'styled' && this.calculator.updatePriceDisplay) {
      const originalUpdatePrice = this.calculator.updatePriceDisplay.bind(this.calculator);
      this.calculator.updatePriceDisplay = (basePrice, setupCosts, drillCosts, stringCosts, totalPrice, pricePerTag) => {
        originalUpdatePrice(basePrice, setupCosts, drillCosts, stringCosts, totalPrice, pricePerTag);
        this.updateWizardPriceDirectly(totalPrice, pricePerTag);
      };
    }
  }
  
  updateStepDisplay() {
    this.cleanupObservers();
    
    const contentContainer = document.getElementById('ht-wizard-step-content');
    if (contentContainer) {
      contentContainer.innerHTML = this.createStepContent();
      this.syncFieldsWithCalculator();
      
      // Sync drill position visibility for Step 2
      if (this.currentStep === 2 && this.calculatorType === 'regular') {
        setTimeout(() => {
          this.syncDrillPositionVisibility();
          this.syncStringTypeVisibility();
        }, 100);
      }

      // Sync String Type visibility for Folded styled Step 2 (Printing & Finishing)
      if (this.currentStep === 2 && this.calculatorType === 'styled' &&
          this.steps[this.currentStep] && this.steps[this.currentStep].id === 'printing-finishing') {
        setTimeout(() => {
          this.syncStringTypeVisibility();
        }, 100);
      }
      
      if (this.currentStep === 1) {
        this.schedulePeriodicSync();
      }
    }
    
    this.updateProgressIndicator();
    this.updateNavigationState();
    
    // Enhanced sync for styled calculator with proper timing
    if (this.calculatorType === 'styled') {
      setTimeout(() => {
        this.syncStyledCalculatorFieldStates();
      }, 300);
      
      setTimeout(() => {
        this.syncStyledCalculatorFieldStates();
      }, 600);
    }
  }
  
  scrollToTop() {
    // Only apply on mobile (matches CSS breakpoint)
    if (window.innerWidth > 768) {
      return;
    }
    
    // Reset scroll position of scrollable containers
    const fieldsGrid = document.querySelector('.ht-wizard-fields-grid');
    if (fieldsGrid) {
      fieldsGrid.scrollTop = 0;
    }
    
    const additionalOptions = document.querySelector('.ht-wizard-additional-options');
    if (additionalOptions) {
      additionalOptions.scrollTop = 0;
    }
    
    // Scroll wizard into view (not page top)
    const wizardOverlay = document.querySelector('.ht-wizard-overlay');
    if (wizardOverlay) {
      wizardOverlay.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  schedulePeriodicSync() {
    let attempts = 0;
    const maxAttempts = 10;
    
    const periodicCheck = () => {
      if (attempts >= maxAttempts) return;
      
      const printSidesCalc = this.getCalculatorField('print_sides');
      const printInkCalc = this.getCalculatorField('print_ink');
      const printSidesWiz = document.getElementById('ht-wizard-print_sides');
      const printInkWiz = document.getElementById('ht-wizard-print_ink');
      
      if (printSidesCalc && printSidesWiz && printSidesCalc.options.length > 1) {
        this.forceSyncFieldOptions(printSidesCalc, printSidesWiz, 'print_sides');
      }
      
      if (printInkCalc && printInkWiz && printInkCalc.options.length > 1) {
        this.forceSyncFieldOptions(printInkCalc, printInkWiz, 'print_ink');
      }
      
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(periodicCheck, 500);
      }
    };
    
    setTimeout(periodicCheck, 100);
  }
  
  updateProgressIndicator() {
    const progressSteps = document.querySelectorAll('.ht-wizard-step[data-step]');
    
    progressSteps.forEach(stepEl => {
      const stepNum = parseInt(stepEl.dataset.step);
      
      stepEl.classList.remove('ht-wizard-step--active', 'ht-wizard-step--completed', 'ht-wizard-step--pending');
      
      if (stepNum < this.currentStep) {
        stepEl.classList.add('ht-wizard-step--completed');
        stepEl.querySelector('.ht-wizard-step-indicator').textContent = '✓';
      } else if (stepNum === this.currentStep) {
        stepEl.classList.add('ht-wizard-step--active');
        stepEl.querySelector('.ht-wizard-step-indicator').textContent = stepNum;
      } else {
        stepEl.classList.add('ht-wizard-step--pending');
        stepEl.querySelector('.ht-wizard-step-indicator').textContent = stepNum;
      }
    });
  }
  
  goToNextStep() {
    // Swallow clicks queued behind a step-identity swap (double-click guard)
    if (this._stepSwapDwellUntil && Date.now() < this._stepSwapDwellUntil) {
      return;
    }
    // Re-derive the step config from current selections before navigating —
    // the cached config goes stale when material/style is set via dropdown.
    // If that swaps the identity of the step the user is on, re-render it and
    // stay put so the visible panel, validation and step list line up again.
    const stepIdBeforeReconfigure = this.steps?.[this.currentStep]?.id;
    const stepIndexBeforeReconfigure = this.currentStep;
    this.reconfigureWizardSteps();
    if (this.currentStep !== stepIndexBeforeReconfigure) {
      // reconfigure clamped currentStep and already re-rendered — stay put
      this._stepSwapDwellUntil = Date.now() + 600;
      return;
    }
    if (this.steps?.[this.currentStep]?.id !== stepIdBeforeReconfigure) {
      this.updateStepDisplay();
      this._stepSwapDwellUntil = Date.now() + 600;
      return;
    }

    const validation = this.validateCurrentStep();
    
    if (!validation.isValid) {
      this.showValidationMessage(validation.errors);
      return;
    }
    
    if (this.currentStep < this.totalSteps) {
      this.currentStep++;
      this.updateStepDisplay();
      this.scrollToTop(); // Scroll to top on step navigation only
    } else {
      this.completeWizard();
    }
  }
  
  goToPreviousStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.updateStepDisplay();
      this.scrollToTop(); // Scroll to top on step navigation only
    }
  }
  
  updateNavigationState() {
    const backBtn = document.getElementById('ht-wizard-back');
    const nextBtn = document.getElementById('ht-wizard-next');
    
    if (backBtn) {
      backBtn.style.display = this.currentStep === 1 ? 'none' : 'block';
    }
    
    if (nextBtn) {
      // Add delay for styled calculator to allow field sync
      if (this.calculatorType === 'styled') {
        setTimeout(() => {
          const validation = this.validateCurrentStep();
          this.applyNavigationState(nextBtn, validation);
        }, 100);
      } else {
        const validation = this.validateCurrentStep();
        this.applyNavigationState(nextBtn, validation);
      }
    }
  }

  // Helper method to apply navigation state
  applyNavigationState(nextBtn, validation) {
    const isLastStep = this.currentStep === this.totalSteps;
    
    if (isLastStep) {
      nextBtn.textContent = 'Next Step - Artwork Stage';
      nextBtn.classList.add('ht-wizard-btn--artwork');
    } else {
      nextBtn.textContent = 'Continue';
      nextBtn.classList.remove('ht-wizard-btn--artwork');
    }
    
    nextBtn.disabled = !validation.isValid;
    nextBtn.classList.toggle('ht-wizard-btn--disabled', !validation.isValid);
  }

  clearWizardSummaryPrices() {
    const exclElement = document.querySelector('.ht-wizard-price-excl');
    const inclElement = document.querySelector('.ht-wizard-price-incl');
    
    if (exclElement) {
      exclElement.innerHTML = '£0.00 <span>excl VAT</span>';
    }
    
    if (inclElement) {
      inclElement.innerHTML = '£0.00 <span>inc VAT</span>';
    }
    
    // Also clear selections grid
    const grid = document.getElementById('ht-wizard-selections-grid');
    if (grid) {
      grid.innerHTML = '';
    }
  }
  
  updateWizardSummary() {
    // Skip updates during initial load cascade
    if (!this.initialLoadComplete || this.isUpdating) return;
    
    // Use immediate execution for styled calculator
    if (this.calculatorType === 'styled') {
      this.updatePriceDisplay();
      this.updateSelectionsDisplay();
      this.updateStatusDisplay();
    } else {
      requestAnimationFrame(() => {
        this.updatePriceDisplay();
        this.updateSelectionsDisplay();
        this.updateStatusDisplay();
      });
    }
  }

  // Add this method to fix the live summary update for styled calculator
  updateWizardPriceDirectly(totalPrice, pricePerTag) {
    // Don't update if we're on step 1 with no material selected
    const materialField = this.getCalculatorField('material');
    if (this.currentStep === 1 && (!materialField || !materialField.value)) {
      return;
    }
    
    const exclElement = document.querySelector('.ht-wizard-price-excl');
    const inclElement = document.querySelector('.ht-wizard-price-incl');
    
    if (totalPrice && totalPrice > 0) {
      // totalPrice is already inc VAT
      const priceInclVAT = totalPrice;
      const priceExclVAT = priceInclVAT / 1.2;
      
      if (exclElement) {
        exclElement.innerHTML = `£${priceExclVAT.toFixed(2)} <span>excl VAT</span>`;
      }
      
      if (inclElement) {
        inclElement.innerHTML = `£${priceInclVAT.toFixed(2)} <span>inc VAT</span>`;
      }
    }
    
    // Also update selections and status
    this.updateSelectionsDisplay();
    this.updateStatusDisplay();
  }
  
  updatePriceDisplay() {
    if (!this.calculator) return;
    
    const exclElement = document.querySelector('.ht-wizard-price-excl');
    const inclElement = document.querySelector('.ht-wizard-price-incl');
    const totalDisplay = document.getElementById('ht-calculator-total-price');
    
    if (totalDisplay && exclElement) {
      const priceInclVATText = totalDisplay.textContent || '£0.00';
      const priceInclVAT = parseFloat(priceInclVATText.replace(/[£Â£]/g, '').replace(/,/g, '')) || 0;
      
      const priceExclVAT = priceInclVAT / 1.2;
      
      if (exclElement) {
        exclElement.innerHTML = `£${priceExclVAT.toFixed(2)} <span>excl VAT</span>`;
      }
      
      if (inclElement) {
        inclElement.innerHTML = `£${priceInclVAT.toFixed(2)} <span>inc VAT</span>`;
      }
    }
  }
  
  updateSelectionsDisplay() {
    const grid = document.getElementById('ht-wizard-selections-grid');
    if (!grid) return;
    
    const selections = this.getCurrentSelections();
    let html = '';
    
    Object.entries(selections).forEach(([key, value]) => {
      if (value && value !== '' && value !== 'None') {
        html += `<div class="ht-wizard-selection-item">${key}: ${value}</div>`;
      }
    });
    
    grid.innerHTML = html;
  }
  
  getCurrentSelections() {
    const selections = {};
    
    // Dynamic field list based on calculator type
    const fields = this.calculatorType === 'styled' ? [
      { id: 'material', label: 'Material' },
      { id: 'style_template', label: 'Style' },
      { id: 'size', label: 'Size' },
      { id: 'quantity', label: 'Qty' },
      { id: 'print_sides', label: 'Sides' },
      { id: 'print_ink', label: 'Ink' },
      { id: 'drill_diameter', label: 'Drill' },
      { id: 'drill_position', label: 'Drill Pos' },
      { id: 'eyelet', label: 'Eyelet' },
      { id: 'string_attachment', label: 'Supply' },
      { id: 'string_type', label: 'String' }
    ] : [
      { id: 'material', label: 'Material' },
      { id: 'shape', label: 'Shape' },
      { id: 'size', label: 'Size' },
      { id: 'quantity', label: 'Qty' },
      { id: 'print_sides', label: 'Sides' },
      { id: 'print_ink', label: 'Ink' },
      { id: 'corner_style', label: 'Corners' },
      { id: 'drill_diameter', label: 'Drill' },
      { id: 'drill_position', label: 'Drill Pos' },
      { id: 'eyelet', label: 'Eyelet' },
      { id: 'string_attachment', label: 'Supply' },
      { id: 'string_type', label: 'String' }
    ];
    
    fields.forEach(field => {
      const element = this.getCalculatorField(field.id);
      if (element && element.value) {
        // Skip size field when shape is custom
        if (field.id === 'size' && this.calculator?.shapeSelect?.value === 'custom') {
          return;
        }

        // Show actual typed number for custom quantity instead of "Custom Quantity" label
        if (field.id === 'quantity' && element.value === 'custom') {
          const customNum = this.calculator?.customQuantityInput?.value;
          if (customNum) {
            selections[field.label] = `${customNum} tags`;
            return;
          }
        }

        const option = element.selectedOptions[0];
        const value = option ? option.textContent : element.value;
        if (value && value !== 'None' && value !== '') {
          selections[field.label] = value;
        }
      }
    });
    
    // Handle custom size
    if (this.calculator?.isCustomSizeActive) {
      const width = document.getElementById('ht-custom-width')?.value;
      const height = document.getElementById('ht-custom-height')?.value;
      if (width && height) {
        selections.Size = `${width}mm × ${height}mm`;
      }
    }
    
    // Add additional options from Step 3 (regular calculator only)
    if (this.calculatorType === 'regular') {
      const additionalLabels = {
        multiple_designs: 'Multi-designs',
        variable_data: 'Variable',
        sequential_data: 'Sequential',
        perforation: 'Perforation'
      };
      
      Object.entries(this.additionalOptions).forEach(([key, value]) => {
        if (value) {
          selections[additionalLabels[key]] = 'Yes';
        }
      });
    }
    
    return selections;
  }
  
  updateStatusDisplay() {
    const statusEl = document.getElementById('ht-wizard-status-text');
    if (!statusEl) return;
    
    const validation = this.validateCurrentStep();
    const remainingFields = validation.errors.length;
    
    if (this.currentStep === this.totalSteps) {
      statusEl.textContent = 'All set. You can proceed.';
      statusEl.classList.add('ht-wizard-status--complete');
    } else if (remainingFields === 0) {
      statusEl.textContent = 'All set. You can proceed.';
      statusEl.classList.add('ht-wizard-status--complete');
    } else {
      // STYLED CALCULATOR: Custom status messages
      if (this.calculatorType === 'styled') {
        if (this.currentStep === 1) {
          if (validation.errors.includes('style_template')) {
            statusEl.textContent = 'Choose a style template to begin.';
          } else if (validation.errors.includes('material')) {
            statusEl.textContent = 'Select a material to continue.';
          } else if (validation.errors.includes('quantity')) {
            statusEl.textContent = 'Choose your quantity to continue.';
          } else {
            statusEl.textContent = `Complete ${remainingFields} more field${remainingFields > 1 ? 's' : ''} to continue.`;
          }
        } else {
          statusEl.textContent = `Complete ${remainingFields} more field${remainingFields > 1 ? 's' : ''} to continue.`;
        }
      } else {
        // Regular calculator status messages
        if (validation.errors.includes('custom_dimensions')) {
          statusEl.textContent = 'Enter valid custom dimensions to continue.';
        } else {
          statusEl.textContent = `Complete ${remainingFields} more field${remainingFields > 1 ? 's' : ''} to continue.`;
        }
      }
      statusEl.classList.remove('ht-wizard-status--complete');
    }
  }
  
  showValidationMessage(errors) {
    const errorMessages = errors.map(e => {
      if (e === 'custom_dimensions') {
        return 'Valid custom dimensions';
      }
      return this.getFieldLabel(e);
    });
    
    const fieldsText = errorMessages.join(', ');
    alert(`Please complete the following: ${fieldsText}`);
  }
  
  completeWizard() {
    // Dispatch event for cart bridge to initialize artwork selector
    document.dispatchEvent(new CustomEvent('wizard:final-complete', {
      detail: {
        calculatorType: this.calculatorType,
        step: this.currentStep,
        isValid: true,
        selections: this.getCurrentSelections()
      }
    }));
    
    // Cart bridge will handle artwork selector from here
  }
  
  createArtworkOptions() {
    return `
      <div class="ht-artwork-options">
        <div class="ht-artwork-option" data-option="upload">
          <div class="ht-artwork-option-icon">📎</div>
          <div class="ht-artwork-option-content">
            <h4>Upload Your Own Artwork</h4>
            <p>Compatible file extensions: ai, eps, pdf, indd, psd, png, jpeg, tif, gif</p>
          </div>
          <div class="ht-artwork-option-arrow">→</div>
        </div>

        <div class="ht-artwork-option" data-option="email_later">
          <div class="ht-artwork-option-icon">📧</div>
          <div class="ht-artwork-option-content">
            <h4>Email Artwork Later</h4>
            <p>Email: artwork@handytags.co.uk</p>
          </div>
          <div class="ht-artwork-option-arrow">→</div>
        </div>
        
        <div class="ht-artwork-option" data-option="design_service">
          <div class="ht-artwork-option-icon">💡</div>
          <div class="ht-artwork-option-content">
            <h4>Design My Artwork For Me</h4>
            <p>Our designers create a design - £25 + VAT</p>
          </div>
          <div class="ht-artwork-option-arrow">→</div>
        </div>
      </div>
    `;
  }

  // Step 3 content and binding (regular calculator only)
  createStep3Content() {
    return `
      <div class="ht-wizard-step-header">
        <h2>Additional</h2>
        <p>Optional production features.</p>
      </div>
      <div class="ht-wizard-step-body">
        <div class="ht-wizard-additional-options">
          
          <!-- Multiple Designs -->
          <div class="ht-wizard-toggle-field">
            <div class="ht-wizard-toggle-content">
              <h4>Multiple Designs</h4>
              <p>Split your order equally across multiple artworks or versions.</p>
            </div>
            <label class="ht-wizard-toggle">
              <input type="checkbox" id="ht-wizard-multiple-designs" data-option="multiple_designs">
              <span class="ht-wizard-toggle-slider"></span>
            </label>
          </div>
          
          <!-- Multiple Designs Entry Field -->
          <div class="ht-wizard-conditional-field" id="ht-wizard-multiple-designs-field" style="display: none;">
            <div class="ht-wizard-field" data-field="multiple_designs_count">
              <input type="number" 
                     id="ht-wizard-multiple-designs-count" 
                     class="ht-wizard-input" 
                     min="2" 
                     max="50" 
                     placeholder="Total Number of Designs"
                     aria-label="Total Number of Designs">
              <div class="ht-wizard-field-note">First design included, £2.50 per additional design</div>
            </div>
          </div>
          
          <!-- Variable Data -->
          <div class="ht-wizard-toggle-field">
            <div class="ht-wizard-toggle-content">
              <h4>Variable data</h4>
              <p>Personalise each tag with unique fields (e.g., names, codes).</p>
            </div>
            <label class="ht-wizard-toggle">
              <input type="checkbox" id="ht-wizard-variable-data" data-option="variable_data">
              <span class="ht-wizard-toggle-slider"></span>
            </label>
          </div>
          
          <!-- Variable Data Instructions -->
          <div class="ht-wizard-conditional-field" id="ht-wizard-variable-data-field" style="display: none;">
            <div class="ht-wizard-info-message ht-wizard-info-message--highlight">
              <span class="ht-wizard-info-icon">📄</span>
              <div>
                To supply us with variable data, download our <a href="https://cdn.shopify.com/s/files/1/0952/4651/6565/files/HT-Variable-Data-Template.xlsx" target="_blank" rel="noopener" download>template file</a>, add your variable data text and send it to
                <strong>artwork@handytags.co.uk</strong> with reference to your order number.
              </div>
            </div>
          </div>
          
          <!-- Sequential Data -->
          <div class="ht-wizard-toggle-field">
            <div class="ht-wizard-toggle-content">
              <h4>Sequential data</h4>
              <p>Auto-increment sequences (e.g., "0001" → "0002") across items.</p>
            </div>
            <label class="ht-wizard-toggle">
              <input type="checkbox" id="ht-wizard-sequential-numbers" data-option="sequential_data">
              <span class="ht-wizard-toggle-slider"></span>
            </label>
          </div>

          <!-- Sequential Data Range Fields -->
          <div class="ht-wizard-conditional-field" id="ht-wizard-sequential-data-field" style="display: none;">
            <div class="ht-wizard-sequential-range">
              <input type="text" 
                     id="ht-wizard-sequential-from" 
                     class="ht-wizard-input" 
                     placeholder="From (e.g., 0001)"
                     aria-label="Sequential numbering start">
              <input type="text" 
                     id="ht-wizard-sequential-to" 
                     class="ht-wizard-input" 
                     placeholder="To (e.g., 1000)"
                     aria-label="Sequential numbering end">
            </div>
          </div>
          
          <!-- Perforation -->
          <div class="ht-wizard-toggle-field">
            <div class="ht-wizard-toggle-content">
              <h4>Perforation</h4>
              <p>Add a single tear-off perforation to your tag design.</p>
            </div>
            <label class="ht-wizard-toggle">
              <input type="checkbox" id="ht-wizard-perforation" data-option="perforation">
              <span class="ht-wizard-toggle-slider"></span>
            </label>
          </div>
          
        </div>
      </div>
    `;
  }

  syncStep3InitialState() {
    this.enableStep3Fields();
    
    const multipleDesignsInput = document.getElementById('ht-multiple-designs');
    if (multipleDesignsInput) {
      const value = parseInt(multipleDesignsInput.value) || 1;
      this.additionalOptions.multiple_designs = value > 1;
    }
    
    const variableDataSelect = document.getElementById('ht-variable-data-select');
    if (variableDataSelect) {
      this.additionalOptions.variable_data = variableDataSelect.value === 'yes';
    }
    
    const sequentialNumbersSelect = document.getElementById('ht-sequential-numbers-select');
    if (sequentialNumbersSelect) {
      this.additionalOptions.sequential_data = sequentialNumbersSelect.value === 'yes';
    }
    
    const perforationCheckbox = document.getElementById('ht-perforation');
    if (perforationCheckbox) {
      this.additionalOptions.perforation = perforationCheckbox.checked;
    }
  }
  
  enableStep3Fields() {
    const fields = [
      'ht-multiple-designs',
      'ht-variable-data-select', 
      'ht-sequential-numbers-select',
      'ht-perforation'
    ];
    
    fields.forEach(fieldId => {
      const field = document.getElementById(fieldId);
      if (field && field.disabled) {
        field.disabled = false;
      }
    });
  }
  
  bindStep3Fields() {
    this.syncStep3InitialState();
    
    const toggles = document.querySelectorAll('.ht-wizard-toggle input[type="checkbox"]');
    toggles.forEach(toggle => {
      const optionName = toggle.dataset.option;
      
      toggle.checked = this.additionalOptions[optionName];
      
      if (toggle.checked) {
        this.showStep3ConditionalField(optionName);
      }
      
      toggle.addEventListener('change', (e) => {
        this.additionalOptions[optionName] = e.target.checked;
        
        if (optionName === 'multiple_designs') {
          const fieldContainer = document.getElementById('ht-wizard-multiple-designs-field');
          const countInput = document.getElementById('ht-wizard-multiple-designs-count');
          
          if (e.target.checked) {
            fieldContainer.style.display = 'block';
            if (countInput && !countInput.value) {
              countInput.value = '2';
            }
            const multipleDesignsInput = document.getElementById('ht-multiple-designs');
            if (multipleDesignsInput) {
              multipleDesignsInput.value = countInput.value || '2';
              multipleDesignsInput.dispatchEvent(new Event('input'));
              multipleDesignsInput.disabled = false;
            }
          } else {
            fieldContainer.style.display = 'none';
            if (countInput) {
              countInput.value = '';
            }
            const multipleDesignsInput = document.getElementById('ht-multiple-designs');
            if (multipleDesignsInput) {
              multipleDesignsInput.value = '1';
              multipleDesignsInput.dispatchEvent(new Event('input'));
            }
          }
        } else if (optionName === 'variable_data') {
          const fieldContainer = document.getElementById('ht-wizard-variable-data-field');
          fieldContainer.style.display = e.target.checked ? 'block' : 'none';
          
          const variableDataSelect = document.getElementById('ht-variable-data-select');
          if (variableDataSelect) {
            variableDataSelect.value = e.target.checked ? 'yes' : 'no';
            variableDataSelect.dispatchEvent(new Event('change'));
            variableDataSelect.disabled = false;
          }
        } else if (optionName === 'sequential_data') {
          const fieldContainer = document.getElementById('ht-wizard-sequential-data-field');
          fieldContainer.style.display = e.target.checked ? 'block' : 'none';
          
          const sequentialNumbersSelect = document.getElementById('ht-sequential-numbers-select');
          if (sequentialNumbersSelect) {
            sequentialNumbersSelect.value = e.target.checked ? 'yes' : 'no';
            sequentialNumbersSelect.dispatchEvent(new Event('change'));
            sequentialNumbersSelect.disabled = false;
          }
          
          if (e.target.checked) {
            this.bindSequentialRangeFields();
          }
        } else if (optionName === 'perforation') {
          const perforationCheckbox = document.getElementById('ht-perforation');
          if (perforationCheckbox) {
            perforationCheckbox.checked = e.target.checked;
            perforationCheckbox.dispatchEvent(new Event('change'));
            perforationCheckbox.disabled = false;
          }
        }
        
        if (this.calculator && this.calculator.calculatePriceIfReady) {
          setTimeout(() => {
            this.calculator.calculatePriceIfReady();
          }, 100);
        }
        
        this.updateWizardSummary();
      });
    });
    
    const multipleDesignsCount = document.getElementById('ht-wizard-multiple-designs-count');
    if (multipleDesignsCount) {
      const multipleToggle = document.getElementById('ht-wizard-multiple-designs');
      if (multipleToggle && multipleToggle.checked && !multipleDesignsCount.value) {
        multipleDesignsCount.value = '2';
      }
      
      multipleDesignsCount.addEventListener('input', (e) => {
        let value = e.target.value;
        
        if (value === '') {
          return;
        }
        
        const numValue = parseInt(value) || 0;
        if (numValue < 2) {
          value = '2';
        } else if (numValue > 50) {
          value = '50';
        } else {
          value = numValue.toString();
        }
        
        const multipleDesignsInput = document.getElementById('ht-multiple-designs');
        if (multipleDesignsInput) {
          multipleDesignsInput.value = value || '1';
          multipleDesignsInput.dispatchEvent(new Event('input'));
        }
      });
      
      multipleDesignsCount.addEventListener('blur', (e) => {
        if (!e.target.value || parseInt(e.target.value) < 2) {
          e.target.value = '2';
          
          const multipleDesignsInput = document.getElementById('ht-multiple-designs');
          if (multipleDesignsInput) {
            multipleDesignsInput.value = '2';
            multipleDesignsInput.dispatchEvent(new Event('input'));
          }
        }
        
        if (this.calculator && this.calculator.calculatePriceIfReady) {
          setTimeout(() => {
            this.calculator.calculatePriceIfReady();
          }, 100);
        }
      });
    }
    
    if (this.additionalOptions.sequential_data) {
      this.bindSequentialRangeFields();
    }
  }

  showStep3ConditionalField(optionName) {
    if (optionName === 'multiple_designs') {
      const fieldContainer = document.getElementById('ht-wizard-multiple-designs-field');
      if (fieldContainer) {
        fieldContainer.style.display = 'block';
      }
    } else if (optionName === 'variable_data') {
      const fieldContainer = document.getElementById('ht-wizard-variable-data-field');
      if (fieldContainer) {
        fieldContainer.style.display = 'block';
      }
    } else if (optionName === 'sequential_data') {
      const fieldContainer = document.getElementById('ht-wizard-sequential-data-field');
      if (fieldContainer) {
        fieldContainer.style.display = 'block';
        this.bindSequentialRangeFields();
      }
    }
  }

  bindSequentialRangeFields() {
    const fromInput = document.getElementById('ht-wizard-sequential-from');
    const toInput = document.getElementById('ht-wizard-sequential-to');
    const rangeInput = document.getElementById('ht-sequential-range');
    
    if (fromInput && toInput && rangeInput) {
      const updateRange = () => {
        const from = fromInput.value || '';
        const to = toInput.value || '';
        rangeInput.value = from && to ? `${from}-${to}` : '';
      };
      
      fromInput.removeEventListener('input', updateRange);
      toInput.removeEventListener('input', updateRange);
      
      fromInput.addEventListener('input', updateRange);
      toInput.addEventListener('input', updateRange);
      
      if (rangeInput.value) {
        const parts = rangeInput.value.split('-');
        if (parts.length === 2) {
          fromInput.value = parts[0].trim();
          toInput.value = parts[1].trim();
        }
      }
    }
  }

  /**
   * Explicitly clear wizard price display to £0.00
   * CRITICAL: Called during reset to prevent stale prices from showing
   */
  clearWizardPriceDisplay() {
    const exclElement = document.querySelector('.ht-wizard-price-excl');
    const inclElement = document.querySelector('.ht-wizard-price-incl');
    
    if (exclElement) {
      exclElement.innerHTML = `£0.00 <span>excl VAT</span>`;
    }
    
    if (inclElement) {
      inclElement.innerHTML = `£0.00 <span>inc VAT</span>`;
    }
    
    // Also clear the calculator's price display textContent to prevent stale reads
    const calcPriceDisplay = document.getElementById('ht-calculator-total-price');
    if (calcPriceDisplay) {
      calcPriceDisplay.textContent = '£0.00';
    }
  }

  /**
   * Complete wizard reset after successful cart addition
   * Stays in wizard mode, resets to Step 1 with clean state
   */
  resetWizardComplete() {
    // Reset to Step 1
    this.currentStep = 1;
    
    // Clear Step 3 additional options state
    this.additionalOptions = {
      multiple_designs: false,
      variable_data: false,
      sequential_data: false,
      perforation: false
    };
    
    // Clear preserved field values
    this.preservedValues = {
      quantity: '',
      print_sides: '',
      print_ink: ''
    };
    
    // Clear state manager FIRST
    if (window.htStateManager && typeof window.htStateManager.clearState === 'function') {
      window.htStateManager.clearState();
    }
    
    // Manually clear all calculator fields and re-trigger cascade
    this.manuallyResetCalculatorFields();
    
    // Call calculator's reset method (belt and braces)
    if (this.calculator && typeof this.calculator.resetCalculator === 'function') {
      this.calculator.resetCalculator();
    }
    
    // Clear price display VALUES (not just hide container)
    this.clearPriceDisplayValues();
    
    // Clear WIZARD live summary prices
    const priceExcl = document.querySelector('.ht-wizard-price-excl');
    const priceIncl = document.querySelector('.ht-wizard-price-incl');
    const selectionsGrid = document.getElementById('ht-wizard-selections-grid');
    const statusText = document.getElementById('ht-wizard-status-text');

    if (priceExcl) priceExcl.innerHTML = '£0.00 <span>excl VAT</span>';
    if (priceIncl) priceIncl.innerHTML = '£0.00 <span>inc VAT</span>';
    if (selectionsGrid) selectionsGrid.innerHTML = '';
    if (statusText) statusText.textContent = 'Choose material to begin.';
    
    this.clearWizardPriceDisplay();
    
    // Update wizard display to show Step 1 (CRITICAL - stays in wizard mode)
    if (this.isWizardMode && this.wizardOverlay) {
      // Small delay to ensure calculator cascade completes
      setTimeout(() => {
        // Render Step 1 content
        this.updateStepDisplay();
        
        // Update progress indicator to show step 1 active
        this.updateProgressIndicator();
        
        // Clear summary sidebar (price already cleared above)
        this.updateWizardSummary();
        this.syncFieldsWithCalculator();
      }, 100);
    }
    this.syncFieldsWithCalculator();
  }

  // Manually clear all calculator fields to default state
  manuallyResetCalculatorFields() {
    // Get calculator type to use correct field IDs
    const fieldIds = this.calculatorType === 'styled' ? {
      material: 'ht-material-select',
      shape: 'ht-shape-select', // Style template in styled calc
      base: 'ht-base-select',
      size: 'ht-size-select',
      quantity: 'ht-quantity-select',
      printSides: 'ht-print-sides-select',
      printInk: 'ht-print-ink-select',
      cornerStyle: 'ht-corner-style-select',
      drillDiameter: 'ht-drill-diameter-select',
      drillPosition: 'ht-drill-position-select',
      eyelet: 'ht-eyelet-select',
      stringAttachment: 'ht-string-attachment-select',
      stringType: 'ht-string-type-select'
    } : {
      material: 'ht-material-select',
      shape: 'ht-shape-select',
      size: 'ht-size-select',
      quantity: 'ht-quantity-select',
      printSides: 'ht-print-sides-select',
      printInk: 'ht-print-ink-select',
      cornerStyle: 'ht-corner-style-select',
      drillDiameter: 'ht-drill-diameter-select',
      drillPosition: 'ht-drill-position-select',
      eyelet: 'ht-eyelet-select',
      stringAttachment: 'ht-string-attachment-select',
      stringType: 'ht-string-type-select'
    };
    
    // Clear each field and trigger change event
    Object.entries(fieldIds).forEach(([fieldName, fieldId]) => {
      const field = document.getElementById(fieldId);
      if (field) {
        // Reset to first option (usually "Choose..." placeholder)
        field.selectedIndex = 0;
        field.value = '';
        
        // Remove any visual selection indicators
        field.classList.remove('selected');
        const checkmark = field.closest('.ht-calculator-field')?.querySelector('.ht-field-checkmark');
        if (checkmark) {
          checkmark.style.display = 'none';
        }
      }
    });
    
    // Clear custom size fields if they exist
    const customWidth = document.getElementById('ht-custom-width');
    const customHeight = document.getElementById('ht-custom-height');
    const customQuantity = document.getElementById('ht-custom-quantity');
    
    if (customWidth) customWidth.value = '';
    if (customHeight) customHeight.value = '';
    if (customQuantity) customQuantity.value = '';
    
    // Hide custom size fields container
    const customSizeFields = document.getElementById('ht-custom-size-fields');
    if (customSizeFields) {
      customSizeFields.style.display = 'none';
    }
    
    // Clear Step 3 fields
    const multipleDesignsInput = document.getElementById('ht-multiple-designs');
    const variableDataSelect = document.getElementById('ht-variable-data-select');
    const sequentialNumbersSelect = document.getElementById('ht-sequential-numbers-select');
    const perforationCheckbox = document.getElementById('ht-perforation');
    
    if (multipleDesignsInput) multipleDesignsInput.value = '1';
    if (variableDataSelect) variableDataSelect.value = 'no';
    if (sequentialNumbersSelect) sequentialNumbersSelect.value = 'no';
    if (perforationCheckbox) perforationCheckbox.checked = false;
  }

  resetArtworkModalState() {
    // Artwork modal controlled by ht-artwork-selector.js
    return;
  }

  // Clear price display VALUES (not just hide container)
  clearPriceDisplayValues() {
    // Find price section container
    const priceSection = document.getElementById('ht-price-section');
    if (!priceSection) {
      return;
    }
    
    // Clear all price value elements
    const priceElements = {
      base: priceSection.querySelector('#ht-base-price'),
      setup: priceSection.querySelector('#ht-setup-cost'),
      total: priceSection.querySelector('#ht-total-price'),
      perTag: priceSection.querySelector('#ht-price-per-tag'),
      vat: priceSection.querySelector('#ht-vat-amount'),
      totalVat: priceSection.querySelector('#ht-total-with-vat')
    };
    
    // Zero out all price displays
    Object.entries(priceElements).forEach(([key, element]) => {
      if (element) {
        element.textContent = '£0.00';
      }
    });
    
    // Also hide the price section to prevent flash
    priceSection.style.display = 'none';
  }
}

// Initialize wizard when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  if (document.querySelector('.ht-calculator-wrapper')) {
    window.htWizard = new HTCalculatorWizard();
  }
});

// Export for global access
window.HTCalculatorWizard = HTCalculatorWizard;